import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/Card';
import { teamLeadDashboardApi, scriptDatabaseApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell, Legend,
} from 'recharts';

// Pivot view is allowlisted to specific people for now. Everyone else sees
// only the flat table. Add more emails here later if access widens.
const PIVOT_VIEW_ALLOWED_EMAILS = new Set([
  'chetan.kumar@pearmediallc.com',
  'ujjwala.srivastava@pearmediallc.com',
]);

interface PerformanceRow {
  id: string;
  name: string;
  // Which ad platform this row came from (Facebook / TikTok / Snapchat / BIGO /
  // NewsBreak / Google). Rows now span platforms, so this disambiguates them.
  platform?: string;
  conforming: boolean;
  convention: string | null;
  vertical: string | null;
  team_lead: string | null;
  media_buyer: string | null;
  // Resolved from media_buyer PML code → user name via our users.pml_code
  // mapping. Null when the code isn't in the directory (or the code is a
  // legacy uppercase nickname like "CHETAN" / "UJJWALA").
  media_buyer_name: string | null;
  // page_code is the same value as editor_code — the 4th convention segment
  // can be interpreted either way. Both columns are showable.
  page_code: string | null;
  // Friendly account name resolved from ad_accounts table; falls back to the
  // raw account_id if no name on file.
  account_id: string | null;
  account_name: string | null;
  campaign_id: string | null;
  script_number: number | null;
  state: string | null;
  creative_type: string | null;
  editor_code: string | null;
  // Spend & revenue
  spend: number;
  rt_cost: number;
  revenue: number;
  profit: number;
  // Engagement
  impressions: number;
  clicks: number;
  link_clicks: number;
  unique_link_clicks: number | null;
  rt_clicks: number;
  unique_clicks: number;
  lp_clicks: number;
  unique_lp_clicks: number;
  lp_views: number;
  // Conversions
  conversions: number;
  rt_ok: number;
  rt_pending: number;
  rt_declined: number;
  approval_rate: number | null;
  // Ratios
  ctr: number;
  unique_ctr: number;
  unique_cpc: number;
  unique_link_click_cpc: number | null;
  cpm: number;
  roas: number;
  roi: number;
  cpa: number;
  rpl: number | null;
  lp_ctr: number;
  lp_view_rate: number | null;
  rt_cpc: number | null;
  hook_rate: number | null;
  hold_rate: number | null;
  avg_play_time: number | null;
  offer_cpc: number | null;
  offer_cr: number | null;
  // Video retention
  video_plays: number;
  video_p25: number;
  video_p50: number;
  video_p75: number;
  video_p100: number;
  video_p25_rate: number | null;
  video_p50_rate: number | null;
  video_p75_rate: number | null;
  video_p100_rate: number | null;
  last_stat_date: string | null;
}

interface Summary {
  spend: number;
  rt_cost: number;
  revenue: number;
  profit: number;
  impressions: number;
  clicks: number;
  link_clicks: number;
  unique_link_clicks: number | null;
  rt_clicks: number;
  unique_clicks: number;
  lp_clicks: number;
  unique_lp_clicks: number;
  lp_views: number;
  conversions: number;
  rt_ok: number;
  rt_pending: number;
  rt_declined: number;
  approval_rate: number | null;
  ctr: number;
  unique_ctr: number;
  unique_cpc: number;
  unique_link_click_cpc: number | null;
  cpm: number;
  roas: number;
  roi: number;
  cpa: number;
  rpl: number | null;
  lp_ctr: number;
  lp_view_rate: number | null;
  rt_cpc: number | null;
  video_plays: number;
  video_p25: number;
  video_p50: number;
  video_p75: number;
  video_p100: number;
  video_p25_rate: number | null;
  video_p50_rate: number | null;
  video_p75_rate: number | null;
  video_p100_rate: number | null;
}

// Per-platform breakdown (ADDITIVE): the same vertical's spend/revenue/profit split across
// every ad platform, not just Facebook. Rides on the main performance response.
interface PlatformBreakdownRow {
  platform: string;
  table: string;
  spend: number;
  revenue: number;
  profit: number;
  conversions: number;
  impressions: number;
  clicks: number;
  roi: number;
  ctr: number;
  cpa: number;
}
interface PlatformBreakdown {
  platforms: PlatformBreakdownRow[];
  totals: (Omit<PlatformBreakdownRow, 'platform' | 'table'>) | null;
}

interface Meta {
  level: 'campaign' | 'ad';
  external_db_configured: boolean;
  row_count: number;
  conforming_count?: number;
  unmatched_count?: number;
  date_from?: string;
  date_to?: string;
  capped_at?: boolean;
  // Echoed back so the UI can render an "only your campaigns" banner when
  // the server forced a filter for scope=self users.
  media_buyer_filter?: string | null;
  scope?: 'all' | 'self' | 'restricted_creator' | null;
  scoped_pml_code?: string | null;
  editor_code?: string | null;
  allowed_columns?: string[];
  // Bucket / highlights window. When matches_user_range = true the UI says
  // "based on your selected range"; otherwise it says "always last 7 days".
  bucket_window_from?: string | null;
  bucket_window_to?: string | null;
  bucket_matches_user_range?: boolean;
}

interface BucketEntry {
  key: string;
  campaign_count: number;
  page_count: number;
  spend: number;
  revenue: number;
  profit: number;
  roi: number;
}

interface Buckets {
  // Tier-split arrays (preferred for new UI — distinct chips per tier)
  winners_a?: BucketEntry[];
  winners_b?: BucketEntry[];
  scale_a?: BucketEntry[];
  scale_b?: BucketEntry[];
  // Combined arrays kept for backwards compat — `winners` = winners_a + winners_b
  winners: BucketEntry[];
  losers: BucketEntry[];
  optimise: BucketEntry[];
  scale: BucketEntry[];
}

// All bucket keys the user can filter the table by (each maps to one of the
// arrays on the Buckets type). Includes the tier-split variants.
type BucketKey =
  | 'winners' | 'winners_a' | 'winners_b'
  | 'losers' | 'optimise'
  | 'scale' | 'scale_a' | 'scale_b';
const BUCKET_LABELS: Record<BucketKey, string> = {
  winners: 'Winner (all)',
  winners_a: 'Winner-A · 7d',
  winners_b: 'Winner-B · 3d',
  losers: 'Loser — Kill',
  optimise: 'Optimise',
  scale: 'Scale & Win (all)',
  scale_a: 'Scale-A · ≥$500',
  scale_b: 'Scale-B · ≥$250',
};

interface HighlightRow {
  id: string;
  name: string;
  convention: string | null;
  editor_code: string | null;
  page_code: string | null;
  media_buyer: string | null;
  media_buyer_name: string | null;
  value: number;
}

interface Highlights {
  top_profit: HighlightRow[];
  top_roi: HighlightRow[];
  top_loss: HighlightRow[];
  top_epc: HighlightRow[];
  // LP EPC = revenue / lp_clicks. Only rendered for users in
  // PIVOT_VIEW_ALLOWED_EMAILS; for others we show top_epc.
  top_lp_epc?: HighlightRow[];
  top_offer_cr: HighlightRow[];
}

type Level = 'campaign' | 'ad';

const fmtMoney = (n: number | null) =>
  n == null ? '—' : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
// Compact integer formatter used by the pivot view to keep the wide table readable.
const formatNumber0 = (n: number) => Math.round(n).toLocaleString();
const fmtNum = (n: number | null) => (n == null ? '—' : Number(n).toLocaleString());
const fmtPct = (n: number | null) => (n == null ? '—' : `${(Number(n) * 100).toFixed(2)}%`);
// hook_rate / hold_rate / offer_cr come from the source DB as fractions
// (0..1), not as percentages. We trust whatever the source provides and only
// scale on the way to the screen — `0.083` → "8.3%", `0.959` → "95.9%".
const fmtRate100 = (n: number | null) => (n == null ? '—' : `${(Number(n) * 100).toFixed(1)}%`);

function defaultRange(): { from: string; to: string } {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 30);
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
}

// Verdict — thresholds aligned with the bucket-chip names so the badge a row
// shows matches the bucket the user selected (no more "SCALE" badge on rows
// that don't actually meet the Scale & Win rule).
//   ROI > 100      → 🌟 Scale & Win   (dark green)
//   ROI > 50       → 🚀 Winner        (green)
//   ROI < -50      → 🛑 Kill          (red)
//  -20 ≤ ROI ≤ 20  → ⚡ Optimise      (amber)
//   else           → neutral          (gray) — "watch" zone, not actionable
function verdictFor(roi: number): { label: string; color: string; bg: string } {
  if (roi > 100) return { label: '🌟 SCALE & WIN', color: '#15803d', bg: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900/50 dark:text-emerald-200' };
  if (roi > 50)  return { label: '🚀 WINNER',     color: '#16a34a', bg: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' };
  if (roi < -50) return { label: '🛑 KILL',       color: '#dc2626', bg: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' };
  if (roi >= -20 && roi <= 20) return { label: '⚡ OPTIMISE', color: '#ca8a04', bg: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' };
  // In-between zones (20-50, -20 to -50) — neither winning nor critical
  return { label: '— watch', color: '#64748b', bg: 'bg-slate-100 text-slate-700 dark:bg-slate-700/40 dark:text-slate-300' };
}

// Column registry — every column the user can show/hide. `always: true` means
// the column is never hidden (identity columns + verdict). Grouped so the
// picker can offer "toggle all in this group".
type ColumnDef = {
  id: string;          // stable id used in localStorage
  label: string;
  group: 'Identity' | 'Money' | 'Conversions' | 'Video' | 'Click Funnel' | 'Offer';
  always?: boolean;    // can't be hidden
};

const COLUMNS: ColumnDef[] = [
  { id: 'name',           label: 'Campaign / Ad Name', group: 'Identity', always: true },
  { id: 'platform',       label: 'Platform',            group: 'Identity' },
  { id: 'convention',     label: 'Convention',          group: 'Identity', always: true },
  { id: 'editor',         label: 'Editor',              group: 'Identity' },
  { id: 'page',           label: 'Page',                group: 'Identity' },
  { id: 'account',        label: 'Account',             group: 'Identity' },
  { id: 'media_buyer',    label: 'Media Buyer',         group: 'Identity' },
  { id: 'spend',          label: 'Spend',               group: 'Money' },
  { id: 'revenue',        label: 'Revenue',             group: 'Money' },
  { id: 'profit',         label: 'Profit',              group: 'Money' },
  { id: 'roi',            label: 'ROI',                 group: 'Money' },
  { id: 'roas',           label: 'ROAS',                group: 'Money' },
  { id: 'rpl',            label: 'RPL',                 group: 'Money' },
  { id: 'conversions',    label: 'Conversions',         group: 'Conversions' },
  { id: 'rt_ok',          label: 'Approved Conv',       group: 'Conversions' },
  { id: 'rt_pending',     label: 'Pending Conv',        group: 'Conversions' },
  { id: 'rt_declined',    label: 'Declined Conv',       group: 'Conversions' },
  { id: 'approval_rate',  label: 'Approval %',          group: 'Conversions' },
  { id: 'cpa',            label: 'CPA',                 group: 'Conversions' },
  { id: 'hook_rate',      label: 'Hook %',              group: 'Video' },
  { id: 'hold_rate',      label: 'Hold %',              group: 'Video' },
  { id: 'avg_play_time',  label: 'Avg Play (s)',        group: 'Video' },
  { id: 'video_plays',    label: 'Plays',               group: 'Video' },
  { id: 'video_p25_rate', label: '25% Retention',       group: 'Video' },
  { id: 'video_p50_rate', label: '50% Retention',       group: 'Video' },
  { id: 'video_p75_rate', label: '75% Retention',       group: 'Video' },
  { id: 'video_p100_rate',label: '100% Retention',      group: 'Video' },
  { id: 'impressions',    label: 'Impressions',         group: 'Click Funnel' },
  { id: 'ctr',            label: 'CTR',                 group: 'Click Funnel' },
  { id: 'clicks',         label: 'Clicks',              group: 'Click Funnel' },
  { id: 'unique_link_clicks',     label: 'Unique Link Clicks',     group: 'Click Funnel' },
  { id: 'unique_link_click_cpc',  label: 'CPC (Unique Link)',      group: 'Click Funnel' },
  { id: 'lp_clicks',      label: 'LP Clicks',           group: 'Click Funnel' },
  { id: 'lp_views',       label: 'LP Views',            group: 'Click Funnel' },
  { id: 'lp_ctr',         label: 'LP CTR',              group: 'Click Funnel' },
  { id: 'lp_view_rate',   label: 'LP View %',           group: 'Click Funnel' },
  { id: 'cpm',            label: 'CPM',                 group: 'Click Funnel' },
  { id: 'offer_cpc',      label: 'Offer CPC',           group: 'Offer' },
  { id: 'offer_cr',       label: 'Offer CR',            group: 'Offer' },
  { id: 'verdict',        label: 'Verdict',             group: 'Identity', always: true },
  // Phase 6: bucket-derived advice. Phase 7: per-row Decline / Done buttons +
  // status pill so the team can record decisions and trigger Slack alerts.
  { id: 'suggestion',     label: 'Suggestion',          group: 'Identity', always: true },
  { id: 'decision',       label: 'Decision',            group: 'Identity', always: true },
];

// Restricted HI creators (Hamza / Ritu / Nainika) only ever see these columns:
// the 10 requested metrics plus name/convention for row identity. No cards,
// no picker, no other values. Enforced client-side AND server-side.
const RESTRICTED_VISIBLE_COLUMNS = new Set<string>([
  'name', 'convention',
  'hook_rate', 'hold_rate', 'unique_link_click_cpc', 'offer_cpc', 'roi',
  'video_p25_rate', 'video_p50_rate', 'video_p75_rate', 'video_p100_rate', 'avg_play_time',
]);

// Suggestion text per bucket — sourced from media-buying playbook.
// (Older static bucket-to-text map removed; suggestionForRow below replaces it.)
// Suggestions are computed per-row from actual metrics (not just bucket label),
// so the column shows distinct advice instead of "all rows say Optimise".
// Build a row-specific suggestion from the actual metrics. Falls through a
// priority ladder so the highest-signal advice wins:
//   1. Clear losers (negative ROI + meaningful spend) → KILL
//   2. Strong winners (high ROI + meaningful spend)  → SCALE
//   3. Approval-rate red flags                       → fix offer/LP
//   4. Engagement red flags (low hook / low CTR)     → fix creative top
//   5. Conversion red flags (low offer CR, high CPA) → fix LP / offer
//   6. Flat ROI                                      → refresh creative
//   7. Otherwise                                     → watch
function suggestionForRow(r: PerformanceRow): string {
  const spend = r.spend || 0;
  const roi   = r.roi || 0;
  const profit = r.profit || 0;
  const approvalRate = r.approval_rate; // null if no decided conversions
  const hookRate = r.hook_rate;          // 0..1
  const offerCr = r.offer_cr;            // 0..1
  const ctr = r.ctr || 0;                // 0..1
  const cpa = r.cpa || 0;
  const conversions = r.conversions || 0;

  // 1. Severe losses
  if (spend >= 500 && roi < -50) {
    return `🛑 KILL — losing $${Math.abs(profit).toFixed(0)} at ${roi.toFixed(0)}% ROI on $${spend.toFixed(0)} spend. Pause this creative for the state.`;
  }
  if (spend >= 250 && roi < -30) {
    return `🛑 Cut spend — ${roi.toFixed(0)}% ROI on $${spend.toFixed(0)} is not recovering. Pause or rotate.`;
  }

  // 2. Strong winners
  if (spend >= 1000 && roi > 100) {
    return `🌟 Scale aggressively — $${profit.toFixed(0)} profit at ${roi.toFixed(0)}% ROI. +30–50% budget today, duplicate into adjacent states.`;
  }
  if (spend >= 500 && roi > 50) {
    return `🚀 Scale +20–30% — clean winner ($${profit.toFixed(0)} profit, ${roi.toFixed(0)}% ROI). Watch CPA the next 24h.`;
  }
  if (spend >= 250 && roi > 50) {
    return `✨ Emerging winner — ${roi.toFixed(0)}% ROI on $${spend.toFixed(0)}. Increase budget gradually; verify with a 3-day signal.`;
  }

  // 3. Approval-rate red flag (offer rejecting your leads)
  if (conversions >= 10 && approvalRate != null && approvalRate < 0.4) {
    return `⚠️ Offer rejection — only ${(approvalRate * 100).toFixed(0)}% approval. Audit lead quality / state targeting before scaling.`;
  }

  // 4. Hook / engagement weakness on a video creative
  if (hookRate != null && hookRate < 0.18 && spend >= 100) {
    return `🎯 Fix the hook — ${(hookRate * 100).toFixed(1)}% hook rate means viewers leave in <3s. Test a new opening before spending more.`;
  }
  if (ctr > 0 && ctr < 0.005 && r.impressions >= 5000) {
    return `🎯 Refresh creative top — CTR ${(ctr * 100).toFixed(2)}% is below floor. Try a new thumbnail / first frame.`;
  }

  // 5. Offer CR / CPA red flags
  if (offerCr != null && offerCr < 0.05 && conversions >= 5) {
    return `💧 LP leakage — offer CR ${(offerCr * 100).toFixed(1)}% means clicks aren't converting. Swap or A/B the LP.`;
  }
  if (cpa > 0 && spend >= 200 && cpa > Math.max(50, spend / Math.max(1, conversions) * 1.5)) {
    return `💰 CPA running hot at $${cpa.toFixed(0)}. Pull spend until offer CR or approval improves.`;
  }

  // 6. Flat / undecided ROI
  if (spend >= 250 && roi >= -20 && roi <= 20) {
    return `⚡ Flat ROI (${roi.toFixed(0)}%) — refresh hook/offer, test a new opening 3s, or swap LP. Don't scale until ROI breaks ±20%.`;
  }

  // 7. Default — just watching
  if (spend < 50) return '— Not enough spend to call. Let it run.';
  return '— Watch — no clear signal yet.';
}

interface CreativeDecision {
  creative_key: string;
  status: 'declined' | 'done';
  note?: string | null;
  decided_at: string;
  decided_by_name?: string | null;
  decided_by_email?: string | null;
}

interface DuplicateEntry {
  id: string;
  name: string;
  media_buyer: string | null;
  account_id: string | null;
  spend: number;
  revenue: number;
  last_stat_date: string | null;
}
interface DuplicateGroup {
  signature: string;
  occurrence_count: number;
  total_spend: number;
  total_revenue: number;
  total_profit: number;
  entries: DuplicateEntry[];
}

interface UnusedScript {
  id: string;
  script_number: string;
  script_title: string;
  vertical: string | null;
  approved: boolean;
  tested: boolean;
  last_tested_at: string | null;
  created_by_name: string | null;
  created_at: string;
}

const COLUMN_PREF_KEY = 'hi_dashboard_hidden_columns_v1';

function loadHiddenColumns(): Set<string> {
  try {
    const raw = localStorage.getItem(COLUMN_PREF_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    if (Array.isArray(arr)) return new Set(arr.filter((s: any) => typeof s === 'string'));
  } catch (_) {}
  return new Set();
}
function saveHiddenColumns(s: Set<string>) {
  try { localStorage.setItem(COLUMN_PREF_KEY, JSON.stringify(Array.from(s))); } catch (_) {}
}

const KPI_TILES = [
  { key: 'spend',       label: 'TOTAL SPEND',  icon: '💸', color: '#ef4444', bg: 'from-red-500/15 to-red-500/0',     fmt: (s: Summary) => fmtMoney(s.spend) },
  { key: 'revenue',     label: 'REVENUE',      icon: '💰', color: '#22c55e', bg: 'from-green-500/15 to-green-500/0', fmt: (s: Summary) => fmtMoney(s.revenue) },
  { key: 'profit',      label: 'PROFIT',       icon: '📈', color: '#0ea5e9', bg: 'from-sky-500/15 to-sky-500/0',     fmt: (s: Summary) => fmtMoney(s.profit) },
  { key: 'roi',         label: 'ROI',          icon: '🎯', color: '#a855f7', bg: 'from-purple-500/15 to-purple-500/0', fmt: (s: Summary) => `${s.roi.toFixed(1)}%` },
  { key: 'conversions', label: 'CONVERSIONS',  icon: '✅', color: '#f97316', bg: 'from-orange-500/15 to-orange-500/0', fmt: (s: Summary) => fmtNum(s.conversions) },
  { key: 'cpa',         label: 'CPA',          icon: '💲', color: '#eab308', bg: 'from-yellow-500/15 to-yellow-500/0', fmt: (s: Summary) => fmtMoney(s.cpa) },
] as const;

const BAR_COLORS = ['#6366f1', '#0ea5e9', '#22c55e', '#f97316', '#ec4899', '#a855f7', '#eab308', '#06b6d4', '#84cc16', '#ef4444'];

export function HomeInsurancePerformance() {
  const { user } = useAuth();
  const userEmail = String((user as any)?.email || '').toLowerCase();
  // Pivot view available to every elevated role (admin / CEO / creative_head /
  // HMB / ATL) plus the original email allowlist (chetan / ujjwala) for
  // back-compat. Self-scope media buyers stay on the flat table.
  const userRole = (user as any)?.role;
  const userAdditionalRoles = ((user as any)?.additional_roles || []) as string[];
  const PIVOT_ELEVATED_ROLES = new Set([
    'admin', 'ceo', 'creative_head', 'head_media_buying', 'assistant_team_lead',
  ]);
  const canUsePivot =
    PIVOT_ELEVATED_ROLES.has(userRole) ||
    userAdditionalRoles.some(r => PIVOT_ELEVATED_ROLES.has(r)) ||
    PIVOT_VIEW_ALLOWED_EMAILS.has(userEmail);

  // Vertical selector — the panel is generalized to any vertical the user may
  // open. Loaded from /team-lead-dashboard/verticals on mount. selectedVertical
  // stays '' until the list resolves, so we never fetch data for a vertical the
  // user can't see (avoids a throwaway 403 on the default). Empty list → the
  // user has no vertical assigned and the panel renders a notice.
  const [verticals, setVerticals] = useState<{ key: string; name: string }[]>([]);
  const [verticalsLoaded, setVerticalsLoaded] = useState(false);
  const [selectedVertical, setSelectedVertical] = useState<string>('');
  const isHomeInsurance = selectedVertical === 'Home Insurance';

  const init = defaultRange();
  const [dateFrom, setDateFrom] = useState(init.from);
  const [dateTo, setDateTo] = useState(init.to);
  const [level, setLevel] = useState<Level>('campaign');
  const [rows, setRows] = useState<PerformanceRow[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [meta, setMeta] = useState<Meta | null>(null);
  const [buckets, setBuckets] = useState<Buckets | null>(null);
  const [highlights, setHighlights] = useState<Highlights | null>(null);
  const [platformBreakdown, setPlatformBreakdown] = useState<PlatformBreakdown | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);
  // Selected bucket filter (null = all rows)
  const [filterBucket, setFilterBucket] = useState<null | BucketKey>(null);

  // Phase 7: decisions persisted server-side per creative_key. Loaded once on
  // mount + after every Decline/Done click. Keyed by creative_key for O(1)
  // lookup when rendering rows.
  const [decisions, setDecisions] = useState<Map<string, CreativeDecision>>(new Map());
  const [savingDecision, setSavingDecision] = useState<string | null>(null);
  // Self-scope users can't decline/done — backend rejects but we hide the UI too.
  const canMakeDecisions = useMemo(() => {
    const role = (user as any)?.role;
    const additional = ((user as any)?.additional_roles || []) as string[];
    const elevated = new Set(['admin', 'ceo', 'creative_head', 'head_media_buying', 'assistant_team_lead']);
    if (elevated.has(role)) return true;
    if (additional.some(r => elevated.has(r))) return true;
    // Allowlisted HI team leads (matches backend HOME_INSURANCE_TEAM_LEAD_EMAILS)
    return PIVOT_VIEW_ALLOWED_EMAILS.has(userEmail);
  }, [user, userEmail]);

  // Phase 9: duplicates panel
  const [duplicates, setDuplicates] = useState<DuplicateGroup[] | null>(null);
  const [duplicatesLoading, setDuplicatesLoading] = useState(false);
  const [showDuplicates, setShowDuplicates] = useState(false);

  // Phase 8: unused HI scripts (tested = FALSE)
  const [unusedScripts, setUnusedScripts] = useState<UnusedScript[] | null>(null);
  const [unusedLoading, setUnusedLoading] = useState(false);
  const [showUnused, setShowUnused] = useState(false);

  // Manual Slack digest trigger (TL-tier only). Used to verify wiring without
  // waiting for the daily cron.
  const [slackTesting, setSlackTesting] = useState(false);

  const [filterMatch, setFilterMatch] = useState<'conforming' | 'unmatched' | 'all'>('conforming');
  const [filterScript, setFilterScript] = useState<string>('all');
  const [filterState, setFilterState] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterEditor, setFilterEditor] = useState<string>('all');
  const [filterMediaBuyer, setFilterMediaBuyer] = useState<string>('all');
  // Two-state search: searchInput is what the user is typing, searchApplied is
  // what's actually filtering. Typing in the box does NOT filter — the user
  // has to click the Search button or press Enter. This matches what they
  // asked for: an explicit search, not auto-filter on every keystroke.
  const [searchInput, setSearchInput] = useState('');
  const [searchApplied, setSearchApplied] = useState('');
  const applySearch = () => setSearchApplied(searchInput);
  const clearSearch = () => { setSearchInput(''); setSearchApplied(''); };

  const [sortKey, setSortKey] = useState<keyof PerformanceRow>('spend');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // Pagination — client-side over the filtered set
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Charts toggle — show / hide the bar charts row
  const [chartsOpen, setChartsOpen] = useState(true);

  // View mode — flat table (default) or pivot tree (Creative > Script > State
  // > Account > Campaign). Pivot rolls up metrics at each level; expand a row
  // to drill deeper.
  const [viewMode, setViewMode] = useState<'flat' | 'pivot'>('flat');
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const toggleNode = (k: string) => setExpandedNodes(prev => {
    const next = new Set(prev);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });

  // Pivot hierarchy order — chetan-only (the only audience that sees pivot).
  // Persisted to localStorage so the picked order survives reloads. Validated
  // on read to guard against an out-of-date saved value (must be a permutation
  // of the 4 dims).
  const [pivotOrder, setPivotOrder] = useState<PivotDim[]>(() => {
    try {
      const saved = localStorage.getItem('hi-pivot-order');
      if (!saved) return DEFAULT_PIVOT_ORDER;
      const parsed = JSON.parse(saved);
      // Allow any 4-dim permutation drawn from PIVOT_DIM_ALL (the user picks
      // 4 of the available dims, in any order). Reject duplicates and unknown
      // values; fall back to default if invalid.
      if (
        Array.isArray(parsed)
        && parsed.length === DEFAULT_PIVOT_ORDER.length
        && parsed.every((d: unknown) => PIVOT_DIM_ALL.includes(d as PivotDim))
        && new Set(parsed).size === parsed.length
      ) return parsed as PivotDim[];
    } catch {}
    return DEFAULT_PIVOT_ORDER;
  });
  const onPivotOrderChange = (next: PivotDim[]) => {
    setPivotOrder(next);
    // Existing keys reference the old order; clear so the user sees a fresh
    // collapsed tree under the new hierarchy.
    setExpandedNodes(new Set());
    try { localStorage.setItem('hi-pivot-order', JSON.stringify(next)); } catch {}
  };
  const expandAllInPivot = () => {
    // No-op if not in pivot mode — set the flag, the renderer reads expandedNodes.
    if (viewMode !== 'pivot') return;
    // We can't enumerate every node here without rebuilding the tree; the
    // PivotTable component handles "expand all" via its own button.
  };

  // Column picker — which table columns the user wants to see. Persisted in
  // localStorage so it survives reloads / new tabs for the same browser.
  // We store HIDDEN ids (not visible) so the default for a new user is "all
  // shown" without needing to ship a defaults array.
  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(() => loadHiddenColumns());
  const [columnPickerOpen, setColumnPickerOpen] = useState(false);
  // Restricted creators are locked to a fixed column whitelist regardless of
  // any saved preference; everyone else keeps their picker-driven visibility.
  const isRestricted = meta?.scope === 'restricted_creator';
  const isVisible = (id: string) =>
    isRestricted ? RESTRICTED_VISIBLE_COLUMNS.has(id) : !hiddenColumns.has(id);
  const setVisible = (id: string, visible: boolean) => {
    setHiddenColumns(prev => {
      const next = new Set(prev);
      if (visible) next.delete(id); else next.add(id);
      saveHiddenColumns(next);
      return next;
    });
  };
  const setGroupVisible = (group: ColumnDef['group'], visible: boolean) => {
    setHiddenColumns(prev => {
      const next = new Set(prev);
      COLUMNS.filter(c => c.group === group && !c.always).forEach(c => {
        if (visible) next.delete(c.id); else next.add(c.id);
      });
      saveHiddenColumns(next);
      return next;
    });
  };
  const showAllColumns = () => { setHiddenColumns(new Set()); saveHiddenColumns(new Set()); };

  const abortRef = useRef<AbortController | null>(null);

  // Load the verticals this user may open, once on mount. Default the selection
  // to the first (the user's own / Home Insurance for HI folks).
  useEffect(() => {
    let cancelled = false;
    teamLeadDashboardApi.getViewableVerticals()
      .then(res => {
        if (cancelled) return;
        const list = (res.data?.data?.verticals || []) as { key: string; name: string }[];
        setVerticals(list);
        setSelectedVertical(prev => prev || (list[0]?.name || ''));
      })
      .catch(() => { if (!cancelled) setVerticals([]); })
      .finally(() => { if (!cancelled) setVerticalsLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!selectedVertical) return; // wait for the vertical list to resolve
    // 150ms debounce — quick enough to feel snappy when picking a date, slow
    // enough to coalesce a date keystroke into a single fetch.
    const handle = setTimeout(() => {
      if (abortRef.current) abortRef.current.abort();
      const ac = new AbortController();
      abortRef.current = ac;

      setLoading(true);
      setError(null);
      teamLeadDashboardApi
        .getHomeInsurancePerformance({ date_from: dateFrom, date_to: dateTo, level, vertical: selectedVertical })
        .then(res => {
          if (ac.signal.aborted) return;
          const d = res.data?.data || {};
          setRows(d.rows || []);
          setSummary(d.summary || null);
          setMeta(d.meta || null);
          setBuckets(d.buckets || null);
          setHighlights(d.highlights || null);
          setPlatformBreakdown(d.platform_breakdown || null);
          setPage(1); // reset to first page on every fetch
        })
        .catch(err => {
          if (ac.signal.aborted) return;
          setError(err?.response?.data?.error || err?.message || 'Failed to load Home Insurance performance');
        })
        .finally(() => {
          if (!ac.signal.aborted) setLoading(false);
        });
    }, 150);
    return () => clearTimeout(handle);
  }, [dateFrom, dateTo, level, refreshNonce, selectedVertical]);

  // Reset to page 1 whenever a filter changes
  useEffect(() => { setPage(1); }, [filterMatch, filterScript, filterState, filterType, filterEditor, filterMediaBuyer, searchApplied, filterBucket]);

  // Load decisions once on mount (and after every successful save). Self-scope
  // users still need to read them to render the status pill — only writing is
  // gated.
  const reloadDecisions = React.useCallback(async () => {
    if (!selectedVertical) return;
    try {
      const res = await teamLeadDashboardApi.listDecisions({ vertical: selectedVertical });
      const map = new Map<string, CreativeDecision>();
      for (const d of (res.data?.data || [])) map.set(d.creative_key, d);
      setDecisions(map);
    } catch (err) {
      // Silent — decisions are non-critical for table rendering
    }
  }, [selectedVertical]);
  useEffect(() => { reloadDecisions(); }, [reloadDecisions, refreshNonce]);

  // (Suggestion text now derived per row via suggestionForRow — no bucket
  // membership lookup needed here.)

  const handleDecision = async (creativeKey: string, status: 'declined' | 'done') => {
    if (!creativeKey) return;
    setSavingDecision(creativeKey);
    try {
      await teamLeadDashboardApi.setDecision({ creative_key: creativeKey, status });
      await reloadDecisions();
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to save decision';
      alert(msg);
    } finally {
      setSavingDecision(null);
    }
  };

  // Phase 9: lazy-load duplicates when the panel is opened.
  const loadDuplicates = React.useCallback(async () => {
    if (!selectedVertical) return;
    setDuplicatesLoading(true);
    try {
      const res = await teamLeadDashboardApi.getDuplicates({ vertical: selectedVertical });
      setDuplicates(res.data?.data || []);
    } catch (err) {
      setDuplicates([]);
    } finally {
      setDuplicatesLoading(false);
    }
  }, [selectedVertical]);
  useEffect(() => { if (showDuplicates && duplicates === null) loadDuplicates(); }, [showDuplicates, duplicates, loadDuplicates]);
  // Changing vertical invalidates the loaded duplicates so the panel reloads
  // for the newly selected vertical, and closes the HI-only Unused Scripts panel.
  useEffect(() => { setDuplicates(null); setShowUnused(false); }, [selectedVertical]);

  // Phase 8: lazy-load unused HI scripts when the panel is opened.
  const loadUnusedScripts = React.useCallback(async () => {
    setUnusedLoading(true);
    try {
      const res = await scriptDatabaseApi.listUnusedHi();
      setUnusedScripts(res.data?.data || []);
    } catch (err) {
      setUnusedScripts([]);
    } finally {
      setUnusedLoading(false);
    }
  }, []);
  useEffect(() => { if (showUnused && unusedScripts === null) loadUnusedScripts(); }, [showUnused, unusedScripts, loadUnusedScripts]);

  // Facets are built from the data WITHOUT the match filter applied — so the
  // dropdowns always show all options even if you flip between conforming /
  // unmatched / all.
  const facets = useMemo(() => {
    const scripts = new Set<string>();
    const states = new Set<string>();
    const types = new Set<string>();
    const editors = new Set<string>();
    const buyers = new Set<string>();
    rows.forEach(r => {
      if (r.script_number != null) scripts.add(`S${r.script_number}`);
      if (r.state) states.add(r.state);
      if (r.creative_type) types.add(r.creative_type);
      if (r.editor_code) editors.add(r.editor_code);
      if (r.media_buyer) buyers.add(r.media_buyer);
    });
    return {
      scripts: Array.from(scripts).sort((a, b) => parseInt(a.slice(1)) - parseInt(b.slice(1))),
      states: Array.from(states).sort(),
      types: Array.from(types).sort(),
      editors: Array.from(editors).sort(),
      buyers: Array.from(buyers).sort(),
    };
  }, [rows]);

  const filtered = useMemo(() => {
    let out = rows;
    if (filterMatch === 'conforming') out = out.filter(r => r.conforming);
    else if (filterMatch === 'unmatched') out = out.filter(r => !r.conforming);
    if (filterScript !== 'all') out = out.filter(r => `S${r.script_number}` === filterScript);
    if (filterState !== 'all') out = out.filter(r => r.state === filterState);
    if (filterType !== 'all') out = out.filter(r => r.creative_type === filterType);
    if (filterEditor !== 'all') out = out.filter(r => r.editor_code === filterEditor);
    if (filterMediaBuyer !== 'all') out = out.filter(r => r.media_buyer === filterMediaBuyer);
    if (searchApplied.trim()) {
      const q = searchApplied.trim().toLowerCase();
      out = out.filter(r => (r.name || '').toLowerCase().includes(q) || (r.convention || '').toLowerCase().includes(q));
    }
    // Bucket filter — keep rows whose creative key matches an entry in the
    // selected bucket. Creative key mirrors how the bucket is grouped on the
    // backend: S<n>-<state>-<type>-<page>.
    if (filterBucket && buckets) {
      const arr = (buckets as any)[filterBucket] as BucketEntry[] | undefined;
      const bucketKeys = new Set((arr || []).map(b => b.key));
      out = out.filter(r => r.convention && bucketKeys.has(r.convention));
    }
    return [...out].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [rows, filterMatch, filterScript, filterState, filterType, filterEditor, filterMediaBuyer, searchApplied, sortKey, sortDir, filterBucket, buckets]);

  // Summary recomputed on whatever filter is currently applied — so KPI tiles
  // reflect the user's view, not the global HI numbers.
  const filteredSummary = useMemo<Summary | null>(() => {
    if (!summary) return null;
    if (filtered.length === rows.length) return summary;
    // unique_link_clicks is null at ad level — tracked via separate flag so we
    // can null-out the final aggregate if no row contributed a real value.
    let ulcHas = false;
    let ulcSum = 0;
    const acc: Summary = {
      spend: 0, rt_cost: 0, revenue: 0, profit: 0,
      impressions: 0, clicks: 0, link_clicks: 0, unique_link_clicks: null,
      rt_clicks: 0, unique_clicks: 0, lp_clicks: 0, unique_lp_clicks: 0, lp_views: 0,
      conversions: 0, rt_ok: 0, rt_pending: 0, rt_declined: 0,
      approval_rate: null,
      ctr: 0, unique_ctr: 0, unique_cpc: 0, unique_link_click_cpc: null, cpm: 0, roas: 0, roi: 0, cpa: 0, rpl: null,
      lp_ctr: 0, lp_view_rate: null, rt_cpc: null,
      video_plays: 0, video_p25: 0, video_p50: 0, video_p75: 0, video_p100: 0,
      video_p25_rate: null, video_p50_rate: null, video_p75_rate: null, video_p100_rate: null,
    };
    filtered.forEach(r => {
      acc.spend += r.spend;
      acc.rt_cost += r.rt_cost;
      acc.revenue += r.revenue;
      acc.profit += r.profit;
      acc.impressions += r.impressions;
      acc.clicks += r.clicks;
      acc.link_clicks += r.link_clicks;
      if (r.unique_link_clicks != null) { ulcSum += r.unique_link_clicks; ulcHas = true; }
      acc.rt_clicks += r.rt_clicks;
      acc.unique_clicks += r.unique_clicks;
      acc.lp_clicks += r.lp_clicks;
      acc.unique_lp_clicks += r.unique_lp_clicks;
      acc.lp_views += r.lp_views;
      acc.conversions += r.conversions;
      acc.rt_ok += r.rt_ok;
      acc.rt_pending += r.rt_pending;
      acc.rt_declined += r.rt_declined;
      acc.video_plays += r.video_plays;
      acc.video_p25 += r.video_p25;
      acc.video_p50 += r.video_p50;
      acc.video_p75 += r.video_p75;
      acc.video_p100 += r.video_p100;
    });
    acc.ctr = acc.impressions > 0 ? acc.clicks / acc.impressions : 0;
    acc.unique_ctr = acc.impressions > 0 ? acc.unique_clicks / acc.impressions : 0;
    acc.unique_cpc = acc.unique_clicks > 0 ? acc.spend / acc.unique_clicks : 0;
    acc.unique_link_clicks = ulcHas ? ulcSum : null;
    acc.unique_link_click_cpc = ulcHas && ulcSum > 0 ? acc.spend / ulcSum : null;
    acc.cpm = acc.impressions > 0 ? (acc.spend / acc.impressions) * 1000 : 0;
    acc.roas = acc.spend > 0 ? acc.revenue / acc.spend : 0;
    acc.roi = acc.spend > 0 ? ((acc.revenue - acc.spend) / acc.spend) * 100 : 0;
    acc.lp_ctr = acc.clicks > 0 ? acc.lp_clicks / acc.clicks : 0;
    acc.lp_view_rate = acc.lp_clicks > 0 ? acc.lp_views / acc.lp_clicks : null;
    acc.cpa = acc.conversions > 0 ? acc.spend / acc.conversions : 0;
    acc.rpl = acc.conversions > 0 ? acc.revenue / acc.conversions : null;
    acc.rt_cpc = acc.rt_clicks > 0 ? acc.spend / acc.rt_clicks : null;
    acc.approval_rate = (acc.rt_ok + acc.rt_declined) > 0
      ? acc.rt_ok / (acc.rt_ok + acc.rt_declined)
      : null;
    acc.video_p25_rate  = acc.video_plays > 0 ? acc.video_p25  / acc.video_plays : null;
    acc.video_p50_rate  = acc.video_plays > 0 ? acc.video_p50  / acc.video_plays : null;
    acc.video_p75_rate  = acc.video_plays > 0 ? acc.video_p75  / acc.video_plays : null;
    acc.video_p100_rate = acc.video_plays > 0 ? acc.video_p100 / acc.video_plays : null;
    return acc;
  }, [filtered, rows, summary]);

  // Top 10 CREATIVES (by spend) for the charts row. We aggregate the per-row
  // metrics by convention key first so the chart shows 10 *different*
  // creatives — without this, 10 campaigns sharing one convention would all
  // render under the same label and the chart looks like one bar repeated.
  const topForCharts = useMemo(() => {
    const byKey = new Map<string, { key: string; spend: number; revenue: number; profit: number }>();
    for (const r of filtered) {
      const key = r.convention || (r.name || '').slice(0, 18) || '(unknown)';
      const existing = byKey.get(key);
      if (existing) {
        existing.spend += r.spend;
        existing.revenue += r.revenue;
        existing.profit += r.profit;
      } else {
        byKey.set(key, { key, spend: r.spend, revenue: r.revenue, profit: r.profit });
      }
    }
    return Array.from(byKey.values())
      .sort((a, b) => b.spend - a.spend)
      .slice(0, 10)
      .map((g, i) => ({
        key: g.key,
        Spend:   Math.round(g.spend),
        Revenue: Math.round(g.revenue),
        Profit:  Math.round(g.profit),
        // Re-derive ROI from the sums (averaging child ROIs would weight rows equally)
        ROI: g.spend > 0 ? Number((((g.revenue - g.spend) / g.spend) * 100).toFixed(1)) : 0,
        idx: i,
      }));
  }, [filtered]);

  // Pagination math
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);

  // Per-page summary — used by the sticky <tfoot> totals row. The KPI tiles
  // above still show full-filtered totals; this gives you "what's on screen
  // right now" totals at the bottom of each page.
  const computePageSummary = (rs: PerformanceRow[]): Summary => {
    let ulcHas = false; let ulcSum = 0;
    const acc: Summary = {
      spend: 0, rt_cost: 0, revenue: 0, profit: 0,
      impressions: 0, clicks: 0, link_clicks: 0, unique_link_clicks: null,
      rt_clicks: 0, unique_clicks: 0, lp_clicks: 0, unique_lp_clicks: 0, lp_views: 0,
      conversions: 0, rt_ok: 0, rt_pending: 0, rt_declined: 0, approval_rate: null,
      ctr: 0, unique_ctr: 0, unique_cpc: 0, unique_link_click_cpc: null, cpm: 0, roas: 0, roi: 0, cpa: 0, rpl: null,
      lp_ctr: 0, lp_view_rate: null, rt_cpc: null,
      video_plays: 0, video_p25: 0, video_p50: 0, video_p75: 0, video_p100: 0,
      video_p25_rate: null, video_p50_rate: null, video_p75_rate: null, video_p100_rate: null,
    };
    rs.forEach(r => {
      acc.spend += r.spend; acc.rt_cost += r.rt_cost; acc.revenue += r.revenue; acc.profit += r.profit;
      acc.impressions += r.impressions; acc.clicks += r.clicks; acc.link_clicks += r.link_clicks;
      if (r.unique_link_clicks != null) { ulcSum += r.unique_link_clicks; ulcHas = true; }
      acc.rt_clicks += r.rt_clicks; acc.unique_clicks += r.unique_clicks;
      acc.lp_clicks += r.lp_clicks; acc.unique_lp_clicks += r.unique_lp_clicks; acc.lp_views += r.lp_views;
      acc.conversions += r.conversions; acc.rt_ok += r.rt_ok; acc.rt_pending += r.rt_pending; acc.rt_declined += r.rt_declined;
      acc.video_plays += r.video_plays;
      acc.video_p25 += r.video_p25; acc.video_p50 += r.video_p50; acc.video_p75 += r.video_p75; acc.video_p100 += r.video_p100;
    });
    acc.ctr = acc.impressions > 0 ? acc.clicks / acc.impressions : 0;
    acc.unique_ctr = acc.impressions > 0 ? acc.unique_clicks / acc.impressions : 0;
    acc.unique_cpc = acc.unique_clicks > 0 ? acc.spend / acc.unique_clicks : 0;
    acc.unique_link_clicks = ulcHas ? ulcSum : null;
    acc.unique_link_click_cpc = ulcHas && ulcSum > 0 ? acc.spend / ulcSum : null;
    acc.cpm = acc.impressions > 0 ? (acc.spend / acc.impressions) * 1000 : 0;
    acc.roas = acc.spend > 0 ? acc.revenue / acc.spend : 0;
    acc.roi = acc.spend > 0 ? ((acc.revenue - acc.spend) / acc.spend) * 100 : 0;
    acc.lp_ctr = acc.clicks > 0 ? acc.lp_clicks / acc.clicks : 0;
    acc.lp_view_rate = acc.lp_clicks > 0 ? acc.lp_views / acc.lp_clicks : null;
    acc.cpa = acc.conversions > 0 ? acc.spend / acc.conversions : 0;
    acc.rpl = acc.conversions > 0 ? acc.revenue / acc.conversions : null;
    acc.rt_cpc = acc.rt_clicks > 0 ? acc.spend / acc.rt_clicks : null;
    acc.approval_rate = (acc.rt_ok + acc.rt_declined) > 0 ? acc.rt_ok / (acc.rt_ok + acc.rt_declined) : null;
    acc.video_p25_rate  = acc.video_plays > 0 ? acc.video_p25  / acc.video_plays : null;
    acc.video_p50_rate  = acc.video_plays > 0 ? acc.video_p50  / acc.video_plays : null;
    acc.video_p75_rate  = acc.video_plays > 0 ? acc.video_p75  / acc.video_plays : null;
    acc.video_p100_rate = acc.video_plays > 0 ? acc.video_p100 / acc.video_plays : null;
    return acc;
  };
  const pageStart = (safePage - 1) * pageSize;
  const pageEnd = Math.min(pageStart + pageSize, filtered.length);
  const pageRows = filtered.slice(pageStart, pageEnd);
  const pageSummary = useMemo(() => computePageSummary(pageRows), [pageRows]);

  const onSort = (key: keyof PerformanceRow) => {
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  // No vertical the user may open → nothing to show.
  if (verticalsLoaded && verticals.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="inline-block w-2 h-6 rounded bg-gradient-to-b from-indigo-500 to-purple-500" />
            Ad Performance
          </CardTitle>
          <CardDescription>No vertical assigned to your account.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 flex-wrap">
              <span className="inline-block w-2 h-6 rounded bg-gradient-to-b from-indigo-500 to-purple-500" />
              <span>{(selectedVertical || 'Ad Performance')} — Ad Performance</span>
              {/* Vertical picker: dropdown when the user can open more than one,
                  a locked label when there's exactly one. */}
              {verticals.length > 1 ? (
                <select
                  value={selectedVertical}
                  onChange={e => setSelectedVertical(e.target.value)}
                  className="ml-1 text-xs font-normal px-2 py-1 border border-border rounded bg-background hover:bg-muted"
                  title="Switch vertical"
                >
                  {verticals.map(v => <option key={v.key} value={v.name}>{v.name}</option>)}
                </select>
              ) : verticals.length === 1 ? (
                <span className="ml-1 text-xs font-medium px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200 border border-indigo-300 dark:border-indigo-700">
                  {verticals[0].name}
                </span>
              ) : null}
            </CardTitle>
            <CardDescription>
              Direct read of {level === 'campaign' ? 'campaigns' : 'ads'} tagged <code className="font-mono text-xs">vertical = {selectedVertical || '—'}</code> in the external ad-platform database. Metrics are the <strong>SUM of daily snapshots</strong> within the chosen date range. Profit = Revenue − Spend (not source rt_profit). Hook/Hold/Offer rates are impression-weighted across days. Synced once per day.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 text-sm flex-wrap">
            <div className="inline-flex rounded border border-border overflow-hidden">
              <button
                onClick={() => setLevel('campaign')}
                className={`px-3 py-1 text-xs font-medium ${level === 'campaign' ? 'bg-indigo-600 text-white' : 'bg-background hover:bg-muted'}`}
              >Campaigns</button>
              <button
                onClick={() => setLevel('ad')}
                className={`px-3 py-1 text-xs font-medium ${level === 'ad' ? 'bg-indigo-600 text-white' : 'bg-background hover:bg-muted'}`}
              >Ads</button>
            </div>
            <label className="text-muted-foreground">From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              className="px-2 py-1 border border-border rounded bg-background"
            />
            <label className="text-muted-foreground">To</label>
            <input
              type="date"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
              className="px-2 py-1 border border-border rounded bg-background"
            />
            <button
              type="button"
              onClick={() => setRefreshNonce(n => n + 1)}
              disabled={loading}
              className="px-3 py-1 text-xs font-medium rounded border border-border bg-background hover:bg-muted disabled:opacity-50"
              title="Re-query the external ad-platform database"
            >
              {loading ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {error && (
          <div className="text-sm text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-3 mb-4">
            {error}
          </div>
        )}

        {/* Personal-scope banner — only shown to media buyers whose view is
            restricted to their own PML's campaigns. Team leads and admins see
            no banner (their scope is 'all'). */}
        {meta && meta.scope === 'self' && meta.scoped_pml_code && (
          <div className="text-sm text-indigo-900 dark:text-indigo-200 bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-700 rounded p-3 mb-4 flex items-start gap-2">
            <span className="text-lg leading-none">👤</span>
            <div>
              <p className="font-semibold">Viewing only your Home Insurance campaigns</p>
              <p className="text-xs mt-0.5 opacity-90">
                Showing all HI rows where <code className="font-mono">media_buyer = {meta.scoped_pml_code}</code>. Your team lead and admins see the full HI dashboard; you only see what's attributed to your PML.
              </p>
            </div>
          </div>
        )}

        {/* Highlights — top 5 per metric. Always rendered when data exists. */}
        {highlights && (
          <div className="mb-4 border border-purple-300 dark:border-purple-700 rounded-lg p-3 bg-gradient-to-br from-purple-50/50 to-transparent dark:from-purple-900/20">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-semibold uppercase tracking-widest text-purple-800 dark:text-purple-300 flex items-center gap-2">
                <span>⭐</span>
                <span>Highlights</span>
              </div>
              <span className="text-[10px] text-muted-foreground">
                {meta && meta.bucket_matches_user_range
                  ? <>Based on your selected range ({meta.bucket_window_from} → {meta.bucket_window_to})</>
                  : <>Always last 7 days ({meta?.bucket_window_from} → {meta?.bucket_window_to})</>}
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
              <HighlightCol title="Top Profit"   accent="green" rows={highlights.top_profit}   format={(v) => fmtMoney(v)} />
              <HighlightCol title="Top ROI"      accent="green" rows={highlights.top_roi}      format={(v) => `${v.toFixed(1)}%`} />
              <HighlightCol title="Top Loss"     accent="red"   rows={highlights.top_loss}     format={(v) => fmtMoney(v)} />
              {canUsePivot && highlights.top_lp_epc
                ? <HighlightCol title="Top LP EPC" accent="blue"  rows={highlights.top_lp_epc}   format={(v) => fmtMoney(v)} />
                : <HighlightCol title="Top EPC"    accent="blue"  rows={highlights.top_epc}      format={(v) => fmtMoney(v)} />}
              <HighlightCol title="Top Offer CR" accent="amber" rows={highlights.top_offer_cr} format={(v) => fmtRate100(v)} />
            </div>
          </div>
        )}

        {/* Bucket chips + dropdown — Probable Winner-A/B / Loser / Optimise / Scale-A/B */}
        {buckets && (
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Predicted action:</span>

            <BucketChip
              icon="🌟"
              label="Scale-A · ≥$500"
              count={(buckets.scale_a || []).length}
              active={filterBucket === 'scale_a'}
              activeClass="bg-emerald-800 text-white border-emerald-800"
              idleClass="bg-emerald-100 text-emerald-900 border-emerald-400 dark:bg-emerald-900/40 dark:text-emerald-200 dark:border-emerald-600"
              onClick={() => setFilterBucket(filterBucket === 'scale_a' ? null : 'scale_a')}
            />
            <BucketChip
              icon="🌱"
              label="Scale-B · ≥$250"
              count={(buckets.scale_b || []).length}
              active={filterBucket === 'scale_b'}
              activeClass="bg-emerald-600 text-white border-emerald-600"
              idleClass="bg-emerald-50 text-emerald-800 border-emerald-300 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-700"
              onClick={() => setFilterBucket(filterBucket === 'scale_b' ? null : 'scale_b')}
            />
            <BucketChip
              icon="🚀"
              label="Winner-A · 7d"
              count={(buckets.winners_a || []).length}
              active={filterBucket === 'winners_a'}
              activeClass="bg-green-700 text-white border-green-700"
              idleClass="bg-green-100 text-green-900 border-green-400 dark:bg-green-900/40 dark:text-green-200 dark:border-green-600"
              onClick={() => setFilterBucket(filterBucket === 'winners_a' ? null : 'winners_a')}
            />
            <BucketChip
              icon="✨"
              label="Winner-B · 3d"
              count={(buckets.winners_b || []).length}
              active={filterBucket === 'winners_b'}
              activeClass="bg-green-500 text-white border-green-500"
              idleClass="bg-green-50 text-green-800 border-green-300 dark:bg-green-900/20 dark:text-green-300 dark:border-green-700"
              onClick={() => setFilterBucket(filterBucket === 'winners_b' ? null : 'winners_b')}
            />
            <BucketChip
              icon="⚡"
              label="Optimise"
              count={(buckets.optimise || []).length}
              active={filterBucket === 'optimise'}
              activeClass="bg-amber-600 text-white border-amber-600"
              idleClass="bg-amber-50 text-amber-800 border-amber-300 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700"
              onClick={() => setFilterBucket(filterBucket === 'optimise' ? null : 'optimise')}
            />
            <BucketChip
              icon="🛑"
              label="Loser — Kill"
              count={(buckets.losers || []).length}
              active={filterBucket === 'losers'}
              activeClass="bg-red-600 text-white border-red-600"
              idleClass="bg-red-50 text-red-800 border-red-300 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700"
              onClick={() => setFilterBucket(filterBucket === 'losers' ? null : 'losers')}
            />

            {/* Dropdown alternative — pick any bucket from a single control */}
            <select
              value={filterBucket || ''}
              onChange={(e) => setFilterBucket(e.target.value ? (e.target.value as BucketKey) : null)}
              className="ml-2 text-xs px-2 py-1 border border-border rounded bg-background hover:bg-muted"
              title="Filter table to a specific bucket"
            >
              <option value="">— pick bucket —</option>
              {(['winners_a','winners_b','losers','optimise','scale_a','scale_b'] as BucketKey[]).map(k => {
                const arr = (buckets as any)[k] as BucketEntry[] | undefined;
                return <option key={k} value={k}>{BUCKET_LABELS[k]} ({(arr || []).length})</option>;
              })}
            </select>

            {filterBucket && (
              <button
                onClick={() => setFilterBucket(null)}
                className="text-xs px-2 py-1 rounded border border-border bg-background hover:bg-muted"
              >
                Clear
              </button>
            )}
          </div>
        )}

        {/* Phase 8 + 9: side panels for HI analytics. Only TL-tier roles see
            these — self-scope buyers don't get the gate. */}
        {meta?.scope === 'all' && (
          <div className="mb-4 flex flex-wrap gap-2">
            <button
              onClick={() => setShowDuplicates(s => !s)}
              className={`text-xs px-3 py-1.5 rounded-full border font-medium ${showDuplicates
                ? 'bg-orange-600 text-white border-orange-600'
                : 'bg-orange-50 text-orange-800 border-orange-300 dark:bg-orange-900/20 dark:text-orange-300 dark:border-orange-700 hover:bg-orange-100'}`}
              title="Find creatives that share the same parsed signature"
            >
              🔁 Duplicates {duplicates ? `(${duplicates.length})` : ''}
            </button>
            {isHomeInsurance && (
            <button
              onClick={() => setShowUnused(s => !s)}
              className={`text-xs px-3 py-1.5 rounded-full border font-medium ${showUnused
                ? 'bg-slate-700 text-white border-slate-700'
                : 'bg-slate-50 text-slate-800 border-slate-300 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-600 hover:bg-slate-100'}`}
              title="HI scripts that have never been tested (no creatives found)"
            >
              📜 Unused Scripts {unusedScripts ? `(${unusedScripts.length})` : ''}
            </button>
            )}
            {/* Slack digests are HI-only in v1. */}
            {isHomeInsurance && (
            <button
              onClick={async () => {
                if (slackTesting) return;
                setSlackTesting(true);
                try {
                  const res = await teamLeadDashboardApi.triggerSlackDigestTest();
                  const d = res.data?.data || {};
                  const ch = d.channel || {};
                  const pu = d.per_user || {};
                  alert(
                    `Slack digest fired.\n\n` +
                    `Channel: ${ch.sent ? '✅ sent' : `❌ ${ch.reason || 'failed'}`}\n` +
                    `Per-user DMs: sent=${pu.sent ?? 0} skipped=${pu.skipped ?? 0} failed=${pu.failed ?? 0}`
                  );
                } catch (err: any) {
                  alert(err?.response?.data?.error || err?.message || 'Slack test failed');
                } finally {
                  setSlackTesting(false);
                }
              }}
              disabled={slackTesting}
              className="text-xs px-3 py-1.5 rounded-full border font-medium bg-indigo-50 text-indigo-800 border-indigo-300 dark:bg-indigo-900/20 dark:text-indigo-300 dark:border-indigo-700 hover:bg-indigo-100 disabled:opacity-50"
              title="Send the HI digest to Slack right now (team channel + per-user DMs)"
            >
              {slackTesting ? '⏳ Sending…' : '💬 Send Slack digest now'}
            </button>
            )}
          </div>
        )}

        {/* Phase 9: duplicates panel */}
        {showDuplicates && meta?.scope === 'all' && (
          <div className="mb-4 border border-orange-300 dark:border-orange-700 rounded-lg p-3 bg-orange-50/50 dark:bg-orange-900/10">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-semibold uppercase tracking-widest text-orange-800 dark:text-orange-300">
                🔁 Duplicate Creatives — same parsed signature (last 30 days)
              </div>
              <button onClick={loadDuplicates} className="text-[10px] underline text-muted-foreground hover:text-foreground">Refresh</button>
            </div>
            {duplicatesLoading ? (
              <div className="text-xs text-muted-foreground py-4 text-center">Loading…</div>
            ) : !duplicates || duplicates.length === 0 ? (
              <div className="text-xs text-muted-foreground py-4 text-center italic">No duplicates found 🎉</div>
            ) : (
              <div className="max-h-80 overflow-y-auto space-y-2">
                {duplicates.map(g => (
                  <div key={g.signature} className="bg-card border border-border rounded p-2">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <code className="font-mono text-xs font-semibold text-orange-900 dark:text-orange-200">{g.signature}</code>
                      <span className="text-[10px] text-muted-foreground">
                        {g.occurrence_count} copies · {fmtMoney(g.total_spend)} spend · {fmtMoney(g.total_profit)} profit
                      </span>
                    </div>
                    <ul className="text-[11px] space-y-0.5 pl-3">
                      {g.entries.map(e => (
                        <li key={e.id} className="flex items-center justify-between gap-2">
                          <span className="font-mono truncate max-w-[28rem]" title={e.name}>{e.name}</span>
                          <span className="text-muted-foreground flex-shrink-0">
                            {fmtMoney(e.spend)} · {e.media_buyer || '—'} · {e.last_stat_date}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Phase 8: unused HI scripts panel */}
        {showUnused && meta?.scope === 'all' && (
          <div className="mb-4 border border-slate-300 dark:border-slate-600 rounded-lg p-3 bg-slate-50/50 dark:bg-slate-800/30">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-700 dark:text-slate-200">
                📜 Unused HI Scripts — never appeared in a creative filename
              </div>
              <button onClick={loadUnusedScripts} className="text-[10px] underline text-muted-foreground hover:text-foreground">Refresh</button>
            </div>
            {unusedLoading ? (
              <div className="text-xs text-muted-foreground py-4 text-center">Loading…</div>
            ) : !unusedScripts || unusedScripts.length === 0 ? (
              <div className="text-xs text-muted-foreground py-4 text-center italic">Every approved HI script has been tested 🎉</div>
            ) : (
              <div className="max-h-80 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="text-muted-foreground border-b border-border">
                    <tr>
                      <th className="text-left py-1 px-2 font-medium">Script #</th>
                      <th className="text-left py-1 px-2 font-medium">Title</th>
                      <th className="text-left py-1 px-2 font-medium">Approved</th>
                      <th className="text-left py-1 px-2 font-medium">Created by</th>
                      <th className="text-left py-1 px-2 font-medium">Last scan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unusedScripts.map(s => (
                      <tr key={s.id} className="border-b border-border/50 last:border-b-0">
                        <td className="py-1 px-2 font-mono font-semibold text-indigo-700 dark:text-indigo-300">{s.script_number}</td>
                        <td className="py-1 px-2 truncate max-w-[24rem]" title={s.script_title}>{s.script_title}</td>
                        <td className="py-1 px-2">
                          {s.approved
                            ? <span className="text-green-700 dark:text-green-400">✓</span>
                            : <span className="text-red-700 dark:text-red-400">flagged</span>}
                        </td>
                        <td className="py-1 px-2">{s.created_by_name || '—'}</td>
                        <td className="py-1 px-2 text-muted-foreground">{s.last_tested_at ? new Date(s.last_tested_at).toLocaleDateString() : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {meta && meta.external_db_configured === false && (
          <div className="text-sm text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded p-3 mb-4">
            External ad-platform database not configured.
          </div>
        )}

        {/* KPI tiles — each with its own color/gradient + emoji.
            Hidden for scope='self' (individual PML-scoped buyers) per product
            spec: they should only see their table-level metrics, not roll-up
            totals like spend / revenue / profit / ROI / conversions / CPA. */}
        {filteredSummary && meta?.scope !== 'self' && !isRestricted && (
          <div className="grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-6 mb-4">
            {KPI_TILES.map(tile => (
              <div
                key={tile.key}
                className={`relative overflow-hidden border border-border rounded-lg p-4 bg-gradient-to-br ${tile.bg}`}
                style={{ borderLeftColor: tile.color, borderLeftWidth: 3 }}
              >
                <div className="absolute right-2 top-1 text-3xl opacity-10 pointer-events-none">{tile.icon}</div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{tile.label}</div>
                <div className="text-2xl font-bold mt-1" style={{ color: tile.color }}>
                  {tile.fmt(filteredSummary)}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* By Platform — ADDITIVE per-platform breakdown of this vertical's spend/revenue/
            profit/ROI. The Facebook deep analysis above is unchanged; this splits the same
            vertical + date range across every ad platform. */}
        {platformBreakdown?.platforms?.length && !isRestricted ? (
          <div className="border border-border rounded-lg p-4 bg-muted/20 mb-4">
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1 flex items-center gap-2">
              <span className="inline-block w-1 h-4 rounded bg-cyan-500" />
              By Platform
            </div>
            <p className="text-[11px] text-muted-foreground mb-3">All ad platforms · {selectedVertical}</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="text-left border-b border-border text-[11px] uppercase tracking-wider text-muted-foreground">
                    <th className="py-2 pr-4 font-semibold">Platform</th>
                    <th className="py-2 pr-4 font-semibold text-right">Spend</th>
                    <th className="py-2 pr-4 font-semibold text-right">Revenue</th>
                    <th className="py-2 pr-4 font-semibold text-right">Profit</th>
                    <th className="py-2 pr-4 font-semibold text-right">ROI</th>
                    <th className="py-2 pr-4 font-semibold text-right">Conversions</th>
                    <th className="py-2 pr-4 font-semibold text-right">Impressions</th>
                    <th className="py-2 pr-0 font-semibold text-right">CTR</th>
                  </tr>
                </thead>
                <tbody>
                  {platformBreakdown.platforms.map(p => (
                    <tr key={p.table} className="border-b border-border/50 hover:bg-muted/30">
                      <td className="py-2 pr-4 font-medium">{p.platform}</td>
                      <td className="py-2 pr-4 text-right tabular-nums">{fmtMoney(p.spend)}</td>
                      <td className="py-2 pr-4 text-right tabular-nums">{fmtMoney(p.revenue)}</td>
                      <td className={`py-2 pr-4 text-right tabular-nums ${p.profit >= 0 ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>{fmtMoney(p.profit)}</td>
                      <td className={`py-2 pr-4 text-right tabular-nums font-semibold ${p.roi >= 0 ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>{p.roi.toFixed(1)}%</td>
                      <td className="py-2 pr-4 text-right tabular-nums">{fmtNum(p.conversions)}</td>
                      <td className="py-2 pr-4 text-right tabular-nums">{fmtNum(p.impressions)}</td>
                      <td className="py-2 pr-0 text-right tabular-nums">{fmtPct(p.ctr)}</td>
                    </tr>
                  ))}
                </tbody>
                {platformBreakdown.totals && (
                  <tfoot>
                    <tr className="border-t-2 border-border font-bold">
                      <td className="py-2 pr-4">Total</td>
                      <td className="py-2 pr-4 text-right tabular-nums">{fmtMoney(platformBreakdown.totals.spend)}</td>
                      <td className="py-2 pr-4 text-right tabular-nums">{fmtMoney(platformBreakdown.totals.revenue)}</td>
                      <td className={`py-2 pr-4 text-right tabular-nums ${platformBreakdown.totals.profit >= 0 ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>{fmtMoney(platformBreakdown.totals.profit)}</td>
                      <td className={`py-2 pr-4 text-right tabular-nums ${platformBreakdown.totals.roi >= 0 ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>{platformBreakdown.totals.roi.toFixed(1)}%</td>
                      <td className="py-2 pr-4 text-right tabular-nums">{fmtNum(platformBreakdown.totals.conversions)}</td>
                      <td className="py-2 pr-4 text-right tabular-nums">{fmtNum(platformBreakdown.totals.impressions)}</td>
                      <td className="py-2 pr-0 text-right tabular-nums">{fmtPct(platformBreakdown.totals.ctr)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        ) : null}

        {/* Bar charts — top 10 by spend */}
        <div className="mb-3 flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            {filtered.length} of {meta?.row_count || 0} {level === 'campaign' ? 'campaigns' : 'ads'}
            {meta?.conforming_count != null && meta?.unmatched_count != null
              ? <> · <span className="text-green-700 dark:text-green-400">{meta.conforming_count} conforming</span> · <span className="text-amber-700 dark:text-amber-400">{meta.unmatched_count} unmatched</span></>
              : null}
            {meta?.capped_at ? ' · capped at 20000' : ''}
          </p>
          <div className="flex items-center gap-2 relative">
            {/* View toggle — flat list vs pivot tree. Only allowlisted emails
                see the toggle. Everyone else is locked to the flat view. */}
            {canUsePivot && (
              <div className="inline-flex rounded border border-border overflow-hidden">
                <button
                  onClick={() => setViewMode('flat')}
                  className={`px-2 py-1 text-xs ${viewMode === 'flat' ? 'bg-indigo-600 text-white' : 'bg-background hover:bg-muted'}`}
                  title="Flat table — one row per campaign"
                >Flat</button>
                <button
                  onClick={() => setViewMode('pivot')}
                  className={`px-2 py-1 text-xs ${viewMode === 'pivot' ? 'bg-indigo-600 text-white' : 'bg-background hover:bg-muted'}`}
                  title="Pivot tree — Creative > Script > State > Account > Campaign"
                >▼ Pivot</button>
              </div>
            )}
            {!isRestricted && (
              <button
                onClick={() => setColumnPickerOpen(o => !o)}
                className="text-xs px-2 py-1 rounded border border-border bg-background hover:bg-muted"
                title="Show or hide table columns"
              >
                ⚙ Columns ({COLUMNS.length - hiddenColumns.size}/{COLUMNS.length})
              </button>
            )}
            {!isRestricted && (
              <button
                onClick={() => setChartsOpen(o => !o)}
                className="text-xs px-2 py-1 rounded border border-border bg-background hover:bg-muted"
              >
                {chartsOpen ? 'Hide charts' : 'Show charts'}
              </button>
            )}

            {!isRestricted && columnPickerOpen && (
              <div
                className="absolute right-0 top-9 z-30 w-80 max-h-[70vh] overflow-y-auto bg-card border border-border rounded-lg shadow-xl p-3 text-xs"
              >
                <div className="flex items-center justify-between mb-2 pb-2 border-b border-border">
                  <span className="font-semibold">Visible columns</span>
                  <div className="flex gap-1">
                    <button
                      onClick={showAllColumns}
                      className="px-2 py-0.5 rounded border border-border hover:bg-muted"
                    >Show all</button>
                    <button
                      onClick={() => setColumnPickerOpen(false)}
                      className="px-2 py-0.5 rounded border border-border hover:bg-muted"
                    >Done</button>
                  </div>
                </div>
                {(['Identity','Money','Conversions','Video','Click Funnel','Offer'] as const).map(group => {
                  const cols = COLUMNS.filter(c => c.group === group);
                  const togglable = cols.filter(c => !c.always);
                  if (cols.length === 0) return null;
                  const allOn  = togglable.every(c => isVisible(c.id));
                  const allOff = togglable.every(c => !isVisible(c.id));
                  return (
                    <div key={group} className="mb-3 last:mb-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{group}</span>
                        {togglable.length > 0 && (
                          <button
                            onClick={() => setGroupVisible(group, !allOn)}
                            className="text-[10px] underline text-indigo-600 dark:text-indigo-400"
                          >
                            {allOn ? 'hide all' : allOff ? 'show all' : 'toggle all'}
                          </button>
                        )}
                      </div>
                      <div className="space-y-0.5 pl-1">
                        {cols.map(c => (
                          <label key={c.id} className={`flex items-center gap-2 ${c.always ? 'opacity-50' : 'cursor-pointer hover:bg-muted/50'} rounded px-1 py-0.5`}>
                            <input
                              type="checkbox"
                              checked={isVisible(c.id)}
                              disabled={c.always}
                              onChange={e => setVisible(c.id, e.target.checked)}
                              className="rounded"
                            />
                            <span>{c.label}{c.always ? <span className="text-muted-foreground"> (always)</span> : null}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {chartsOpen && !isRestricted && topForCharts.length > 0 && (
          <div className="grid gap-3 lg:grid-cols-2 mb-4">
            <div className="border border-border rounded-lg p-3 bg-muted/20">
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-2">
                <span className="inline-block w-1 h-4 rounded bg-indigo-500" />
                Top 10 — Spend vs Revenue vs Profit
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={topForCharts} margin={{ top: 5, right: 10, left: 0, bottom: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(120,120,120,0.2)" vertical={false} />
                  <XAxis dataKey="key" tick={{ fontSize: 10, fill: 'currentColor' }} angle={-25} textAnchor="end" interval={0} height={60} />
                  <YAxis tick={{ fontSize: 10, fill: 'currentColor' }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip contentStyle={{ fontSize: 11 }} formatter={(v: number) => fmtMoney(v)} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  <Bar dataKey="Spend" fill="#ef4444" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Revenue" fill="#22c55e" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Profit" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="border border-border rounded-lg p-3 bg-muted/20">
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-2">
                <span className="inline-block w-1 h-4 rounded bg-green-500" />
                Top 10 — ROI %
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={topForCharts} margin={{ top: 5, right: 10, left: 0, bottom: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(120,120,120,0.2)" vertical={false} />
                  <XAxis dataKey="key" tick={{ fontSize: 10, fill: 'currentColor' }} angle={-25} textAnchor="end" interval={0} height={60} />
                  <YAxis tick={{ fontSize: 10, fill: 'currentColor' }} tickFormatter={(v) => `${v}%`} />
                  <Tooltip contentStyle={{ fontSize: 11 }} formatter={(v: number) => `${v.toFixed(1)}%`} />
                  <Bar dataKey="ROI" radius={[8, 8, 0, 0]}>
                    {topForCharts.map((entry, i) => (
                      <Cell key={i} fill={entry.ROI < 0 ? '#ef4444' : entry.ROI > 40 ? '#16a34a' : entry.ROI > 20 ? '#0ea5e9' : '#eab308'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Filter row */}
        <div className="flex flex-wrap gap-2 mb-3">
          <label className="inline-flex items-center gap-1 text-xs">
            <span className="text-muted-foreground">Match:</span>
            <select
              value={filterMatch}
              onChange={e => setFilterMatch(e.target.value as 'conforming' | 'unmatched' | 'all')}
              className="px-2 py-1 border border-border rounded bg-background"
            >
              <option value="conforming">Conforming</option>
              <option value="unmatched">Unmatched regex</option>
              <option value="all">All HI {level === 'campaign' ? 'campaigns' : 'ads'}</option>
            </select>
          </label>
          <FilterSelect label="Script" value={filterScript} onChange={setFilterScript} options={facets.scripts} />
          <FilterSelect label="State" value={filterState} onChange={setFilterState} options={facets.states} />
          <FilterSelect label="Type" value={filterType} onChange={setFilterType} options={facets.types} />
          {/* Editor and Page are aliases of the same convention segment — both
              are shown so the user can pick the lens they prefer (editor code
              vs FB page identifier). Selecting either narrows by that code. */}
          <FilterSelect label="Editor" value={filterEditor} onChange={setFilterEditor} options={facets.editors} />
          <FilterSelect label="Page"   value={filterEditor} onChange={setFilterEditor} options={facets.editors} />
          <FilterSelect label="Media Buyer" value={filterMediaBuyer} onChange={setFilterMediaBuyer} options={facets.buyers} />
          <div className="inline-flex items-center gap-1">
            <input
              type="text"
              placeholder="Search name or convention…"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') { e.preventDefault(); applySearch(); }
                if (e.key === 'Escape') { e.preventDefault(); clearSearch(); }
              }}
              className="px-2 py-1 border border-border rounded bg-background text-xs w-56"
            />
            <button
              type="button"
              onClick={applySearch}
              disabled={searchInput === searchApplied}
              className="px-3 py-1 text-xs font-medium rounded bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-40 disabled:cursor-not-allowed"
              title="Search (Enter)"
            >
              🔍 Search
            </button>
            {(searchInput || searchApplied) && (
              <button
                type="button"
                onClick={clearSearch}
                className="px-2 py-1 text-xs rounded border border-border bg-background hover:bg-muted"
                title="Clear (Esc)"
              >
                ✕
              </button>
            )}
            {searchApplied && (
              <span className="text-[10px] text-muted-foreground ml-1">filtering: <code className="font-mono">{searchApplied}</code></span>
            )}
          </div>
        </div>

        {loading ? (
          <div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground">
            No {level === 'campaign' ? 'campaigns' : 'ads'} found for Home Insurance with the current filters.
          </div>
        ) : (viewMode === 'pivot' && canUsePivot) ? (
          <PivotTable
            rows={filtered}
            expanded={expandedNodes}
            onToggle={toggleNode}
            onExpandAll={() => {
              // Expand every node by precomputing a Set of all keys in the tree.
              setExpandedNodes(collectAllPivotKeys(filtered, pivotOrder));
            }}
            onCollapseAll={() => setExpandedNodes(new Set())}
            verdictFor={verdictFor}
            order={pivotOrder}
            onOrderChange={onPivotOrderChange}
          />
        ) : (
          <>
            <div className="overflow-x-auto border border-border rounded">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    {isVisible('name')         && <Th>{level === 'campaign' ? 'Campaign Name' : 'Ad Name'}</Th>}
                    {isVisible('platform')     && <Th>Platform</Th>}
                    {isVisible('convention')   && <Th>Convention</Th>}
                    {isVisible('editor')       && <Th>Editor</Th>}
                    {isVisible('page')         && <Th>Page</Th>}
                    {isVisible('account')      && <Th>Account</Th>}
                    {isVisible('media_buyer')  && <Th>Buyer</Th>}
                    {/* Money */}
                    {isVisible('spend')        && <ThSort label="Spend" k="spend" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />}
                    {isVisible('revenue')      && <ThSort label="Revenue" k="revenue" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />}
                    {isVisible('profit')       && <ThSort label="Profit" k="profit" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />}
                    {isVisible('roi')          && <ThSort label="ROI" k="roi" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />}
                    {isVisible('roas')         && <ThSort label="ROAS" k="roas" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />}
                    {isVisible('rpl')          && <ThSort label="RPL" k="rpl" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />}
                    {/* Conversions */}
                    {isVisible('conversions')  && <ThSort label="Conv" k="conversions" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />}
                    {isVisible('rt_ok')        && <ThSort label="Approved" k="rt_ok" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />}
                    {isVisible('rt_pending')   && <ThSort label="Pend" k="rt_pending" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />}
                    {isVisible('rt_declined')  && <ThSort label="Decl" k="rt_declined" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />}
                    {isVisible('approval_rate')&& <ThSort label="Appr%" k="approval_rate" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />}
                    {isVisible('cpa')          && <ThSort label="CPA" k="cpa" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />}
                    {/* Video */}
                    {isVisible('hook_rate')      && <ThSort label="Hook" k="hook_rate" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />}
                    {isVisible('hold_rate')      && <ThSort label="Hold" k="hold_rate" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />}
                    {isVisible('avg_play_time')  && <ThSort label="Avg Play" k="avg_play_time" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />}
                    {isVisible('video_plays')    && <ThSort label="Plays" k="video_plays" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />}
                    {isVisible('video_p25_rate') && <ThSort label="25%" k="video_p25_rate" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />}
                    {isVisible('video_p50_rate') && <ThSort label="50%" k="video_p50_rate" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />}
                    {isVisible('video_p75_rate') && <ThSort label="75%" k="video_p75_rate" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />}
                    {isVisible('video_p100_rate')&& <ThSort label="100%" k="video_p100_rate" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />}
                    {/* Click funnel */}
                    {isVisible('impressions')  && <ThSort label="Impr" k="impressions" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />}
                    {isVisible('ctr')          && <ThSort label="CTR" k="ctr" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />}
                    {isVisible('clicks')       && <ThSort label="Clicks" k="clicks" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />}
                    {isVisible('unique_link_clicks')    && <ThSort label="Unique Link Clicks" k="unique_link_clicks" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />}
                    {isVisible('unique_link_click_cpc') && <ThSort label="CPC (Unique Link)" k="unique_link_click_cpc" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />}
                    {isVisible('lp_clicks')    && <ThSort label="LP Clicks" k="lp_clicks" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />}
                    {isVisible('lp_views')     && <ThSort label="LP Views" k="lp_views" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />}
                    {isVisible('lp_ctr')       && <ThSort label="LP CTR" k="lp_ctr" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />}
                    {isVisible('lp_view_rate') && <ThSort label="LP View %" k="lp_view_rate" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />}
                    {isVisible('cpm')          && <ThSort label="CPM" k="cpm" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />}
                    {isVisible('offer_cpc')    && <ThSort label="Offer CPC" k="offer_cpc" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />}
                    {isVisible('offer_cr')     && <ThSort label="Offer CR" k="offer_cr" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />}
                    {isVisible('verdict')      && <Th>Verdict</Th>}
                    {isVisible('suggestion')   && <Th>Suggestion</Th>}
                    {isVisible('decision')     && <Th>Decision</Th>}
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map(r => {
                    const v = verdictFor(r.roi);
                    return (
                      <tr key={r.id} className="border-t border-border hover:bg-muted/30">
                        {isVisible('name') && (
                          <Td title={r.name}>
                            {/* Ellipsis at ~28 chars — hover the cell to see the full name */}
                            <span className="font-mono text-xs inline-block max-w-[14rem] truncate align-middle">{r.name || '—'}</span>
                          </Td>
                        )}
                        {isVisible('platform') && <Td>{r.platform || '—'}</Td>}
                        {isVisible('convention') && (
                          <Td title={r.convention || ''}>
                            <span className="font-mono text-xs font-medium text-indigo-700 dark:text-indigo-300">{r.convention || '—'}</span>
                          </Td>
                        )}
                        {isVisible('editor') && <Td>{r.editor_code || '—'}</Td>}
                        {isVisible('page')   && <Td>{r.page_code   || '—'}</Td>}
                        {isVisible('account') && (
                          <Td title={r.account_name || r.account_id || ''}>
                            {/* Account name truncated to ~10 chars, hover for full */}
                            {r.account_name || r.account_id ? (
                              <span className="inline-block max-w-[9rem] truncate align-middle">{r.account_name || r.account_id}</span>
                            ) : '—'}
                          </Td>
                        )}
                        {isVisible('media_buyer') && (
                          <Td title={r.media_buyer || ''}>
                            {r.media_buyer_name ? (
                              <span>
                                <span className="font-medium">{r.media_buyer_name}</span>
                                <span className="ml-1 text-[10px] text-muted-foreground font-mono">({r.media_buyer})</span>
                              </span>
                            ) : (
                              <span className="font-mono text-xs">{r.media_buyer || '—'}</span>
                            )}
                          </Td>
                        )}
                        {/* Money */}
                        {isVisible('spend') && <Td align="right" className="text-red-700 dark:text-red-400">{r.spend ? fmtMoney(r.spend) : '—'}</Td>}
                        {isVisible('revenue') && <Td align="right" className="text-green-700 dark:text-green-400">{r.revenue ? fmtMoney(r.revenue) : '—'}</Td>}
                        {isVisible('profit') && (
                          <Td align="right" className={r.profit < 0 ? 'text-red-600 dark:text-red-400 font-medium' : r.profit > 0 ? 'text-sky-700 dark:text-sky-400 font-medium' : ''}>
                            {r.spend || r.revenue ? fmtMoney(r.profit) : '—'}
                          </Td>
                        )}
                        {isVisible('roi') && (
                          <Td align="right" className={r.roi < 0 ? 'text-red-600 dark:text-red-400 font-medium' : r.roi > 40 ? 'text-green-700 dark:text-green-400 font-bold' : r.roi > 20 ? 'text-sky-700 dark:text-sky-400' : 'text-amber-700 dark:text-amber-400'}>
                            {r.spend ? `${r.roi.toFixed(1)}%` : '—'}
                          </Td>
                        )}
                        {isVisible('roas') && <Td align="right">{r.spend ? `${r.roas.toFixed(2)}x` : '—'}</Td>}
                        {isVisible('rpl')  && <Td align="right">{r.conversions ? fmtMoney(r.rpl) : '—'}</Td>}
                        {/* Conversions */}
                        {isVisible('conversions')   && <Td align="right">{r.conversions ? fmtNum(r.conversions) : '—'}</Td>}
                        {isVisible('rt_ok')         && <Td align="right" className="text-green-700 dark:text-green-400">{r.rt_ok ? fmtNum(r.rt_ok) : '—'}</Td>}
                        {isVisible('rt_pending')    && <Td align="right" className="text-amber-700 dark:text-amber-400">{r.rt_pending ? fmtNum(r.rt_pending) : '—'}</Td>}
                        {isVisible('rt_declined')   && <Td align="right" className="text-red-700 dark:text-red-400">{r.rt_declined ? fmtNum(r.rt_declined) : '—'}</Td>}
                        {isVisible('approval_rate') && <Td align="right">{fmtPct(r.approval_rate)}</Td>}
                        {isVisible('cpa')           && <Td align="right">{r.conversions ? fmtMoney(r.cpa) : '—'}</Td>}
                        {/* Video */}
                        {isVisible('hook_rate')       && <Td align="right">{fmtRate100(r.hook_rate)}</Td>}
                        {isVisible('hold_rate')       && <Td align="right">{fmtRate100(r.hold_rate)}</Td>}
                        {isVisible('avg_play_time')   && <Td align="right">{r.avg_play_time != null ? `${r.avg_play_time.toFixed(1)}s` : '—'}</Td>}
                        {isVisible('video_plays')     && <Td align="right">{r.video_plays ? fmtNum(r.video_plays) : '—'}</Td>}
                        {isVisible('video_p25_rate')  && <Td align="right">{fmtPct(r.video_p25_rate)}</Td>}
                        {isVisible('video_p50_rate')  && <Td align="right">{fmtPct(r.video_p50_rate)}</Td>}
                        {isVisible('video_p75_rate')  && <Td align="right">{fmtPct(r.video_p75_rate)}</Td>}
                        {isVisible('video_p100_rate') && <Td align="right">{fmtPct(r.video_p100_rate)}</Td>}
                        {/* Click funnel */}
                        {isVisible('impressions')  && <Td align="right">{r.impressions ? fmtNum(r.impressions) : '—'}</Td>}
                        {isVisible('ctr')          && <Td align="right">{r.impressions ? fmtPct(r.ctr) : '—'}</Td>}
                        {isVisible('clicks')       && <Td align="right">{r.clicks ? fmtNum(r.clicks) : '—'}</Td>}
                        {isVisible('unique_link_clicks')    && <Td align="right">{r.unique_link_clicks != null ? fmtNum(r.unique_link_clicks) : '—'}</Td>}
                        {isVisible('unique_link_click_cpc') && <Td align="right">{r.unique_link_click_cpc != null ? fmtMoney(r.unique_link_click_cpc) : '—'}</Td>}
                        {isVisible('lp_clicks')    && <Td align="right">{r.lp_clicks ? fmtNum(r.lp_clicks) : '—'}</Td>}
                        {isVisible('lp_views')     && <Td align="right">{r.lp_views ? fmtNum(r.lp_views) : '—'}</Td>}
                        {isVisible('lp_ctr')       && <Td align="right">{r.clicks ? fmtPct(r.lp_ctr) : '—'}</Td>}
                        {isVisible('lp_view_rate') && <Td align="right">{fmtPct(r.lp_view_rate)}</Td>}
                        {isVisible('cpm')          && <Td align="right">{r.impressions ? fmtMoney(r.cpm) : '—'}</Td>}
                        {isVisible('offer_cpc')    && <Td align="right">{r.offer_cpc != null ? fmtMoney(r.offer_cpc) : '—'}</Td>}
                        {isVisible('offer_cr')     && <Td align="right">{fmtRate100(r.offer_cr)}</Td>}
                        {isVisible('verdict') && (
                          <Td>
                            <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${v.bg}`}>{v.label}</span>
                          </Td>
                        )}
                        {isVisible('suggestion') && (
                          <Td>
                            {(() => {
                              const txt = suggestionForRow(r);
                              return (
                                <span className="text-[11px] leading-tight inline-block max-w-[20rem] whitespace-normal" title={txt}>
                                  {txt}
                                </span>
                              );
                            })()}
                          </Td>
                        )}
                        {isVisible('decision') && (
                          <Td>
                            {(() => {
                              const key = r.convention;
                              if (!key) return <span className="text-muted-foreground text-xs italic">—</span>;
                              const d = decisions.get(key);
                              const isSaving = savingDecision === key;
                              if (d) {
                                const pillBg = d.status === 'declined'
                                  ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200'
                                  : 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200';
                                return (
                                  <div className="flex items-center gap-1">
                                    <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${pillBg}`}>
                                      {d.status === 'declined' ? '🛑 Declined' : '✅ Done'}
                                    </span>
                                    {canMakeDecisions && (
                                      <button
                                        onClick={() => handleDecision(key, d.status === 'declined' ? 'done' : 'declined')}
                                        disabled={isSaving}
                                        className="text-[10px] underline text-muted-foreground hover:text-foreground disabled:opacity-40"
                                        title="Flip the decision"
                                      >
                                        {isSaving ? '…' : 'flip'}
                                      </button>
                                    )}
                                  </div>
                                );
                              }
                              if (!canMakeDecisions) {
                                return <span className="text-muted-foreground text-xs italic">—</span>;
                              }
                              return (
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={() => handleDecision(key, 'declined')}
                                    disabled={isSaving}
                                    className="px-2 py-0.5 text-[10px] font-semibold rounded border border-red-300 text-red-700 hover:bg-red-50 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-900/30 disabled:opacity-40"
                                    title="Decline — record + Slack alert"
                                  >🛑 Decline</button>
                                  <button
                                    onClick={() => handleDecision(key, 'done')}
                                    disabled={isSaving}
                                    className="px-2 py-0.5 text-[10px] font-semibold rounded border border-green-300 text-green-700 hover:bg-green-50 dark:border-green-700 dark:text-green-300 dark:hover:bg-green-900/30 disabled:opacity-40"
                                    title="Done — record + Slack alert"
                                  >✅ Done</button>
                                </div>
                              );
                            })()}
                          </Td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
                {/* Phase 5: sticky totals footer. Shows totals for the CURRENT
                    PAGE only (so you see "what's on screen right now"). KPI
                    tiles above show the full-filtered totals. Sticky so it
                    stays visible while scrolling the table body. */}
                {pageSummary && pageRows.length > 0 && (
                  <tfoot className="sticky bottom-0 bg-card border-t-2 border-border z-10 shadow-[0_-2px_4px_rgba(0,0,0,0.06)]">
                    <tr className="font-semibold">
                      {isVisible('name') && <Td><span className="text-xs uppercase tracking-wider text-muted-foreground">Page totals · {pageRows.length} of {filtered.length}</span></Td>}
                      {isVisible('platform') && <Td>—</Td>}
                      {isVisible('convention') && <Td>—</Td>}
                      {isVisible('editor') && <Td>—</Td>}
                      {isVisible('page') && <Td>—</Td>}
                      {isVisible('account') && <Td>—</Td>}
                      {isVisible('media_buyer') && <Td>—</Td>}
                      {isVisible('spend') && <Td align="right" className="text-red-700 dark:text-red-400">{fmtMoney(pageSummary.spend)}</Td>}
                      {isVisible('revenue') && <Td align="right" className="text-green-700 dark:text-green-400">{fmtMoney(pageSummary.revenue)}</Td>}
                      {isVisible('profit') && (
                        <Td align="right" className={pageSummary.profit < 0 ? 'text-red-600 dark:text-red-400' : 'text-sky-700 dark:text-sky-400'}>
                          {fmtMoney(pageSummary.profit)}
                        </Td>
                      )}
                      {isVisible('roi') && (
                        <Td align="right" className={pageSummary.roi < 0 ? 'text-red-600 dark:text-red-400' : pageSummary.roi > 40 ? 'text-green-700 dark:text-green-400' : 'text-amber-700 dark:text-amber-400'}>
                          {`${pageSummary.roi.toFixed(1)}%`}
                        </Td>
                      )}
                      {isVisible('roas') && <Td align="right">{`${pageSummary.roas.toFixed(2)}x`}</Td>}
                      {isVisible('rpl') && <Td align="right">{pageSummary.conversions ? fmtMoney(pageSummary.rpl) : '—'}</Td>}
                      {isVisible('conversions') && <Td align="right">{fmtNum(pageSummary.conversions)}</Td>}
                      {isVisible('rt_ok') && <Td align="right" className="text-green-700 dark:text-green-400">{fmtNum(pageSummary.rt_ok)}</Td>}
                      {isVisible('rt_pending') && <Td align="right" className="text-amber-700 dark:text-amber-400">{fmtNum(pageSummary.rt_pending)}</Td>}
                      {isVisible('rt_declined') && <Td align="right" className="text-red-700 dark:text-red-400">{fmtNum(pageSummary.rt_declined)}</Td>}
                      {isVisible('approval_rate') && <Td align="right">{fmtPct(pageSummary.approval_rate)}</Td>}
                      {isVisible('cpa') && <Td align="right">{pageSummary.conversions ? fmtMoney(pageSummary.cpa) : '—'}</Td>}
                      {isVisible('hook_rate') && <Td align="right">—</Td>}
                      {isVisible('hold_rate') && <Td align="right">—</Td>}
                      {isVisible('avg_play_time') && <Td align="right">—</Td>}
                      {isVisible('video_plays') && <Td align="right">{fmtNum(pageSummary.video_plays)}</Td>}
                      {isVisible('video_p25_rate') && <Td align="right">{fmtPct(pageSummary.video_p25_rate)}</Td>}
                      {isVisible('video_p50_rate') && <Td align="right">{fmtPct(pageSummary.video_p50_rate)}</Td>}
                      {isVisible('video_p75_rate') && <Td align="right">{fmtPct(pageSummary.video_p75_rate)}</Td>}
                      {isVisible('video_p100_rate') && <Td align="right">{fmtPct(pageSummary.video_p100_rate)}</Td>}
                      {isVisible('impressions') && <Td align="right">{fmtNum(pageSummary.impressions)}</Td>}
                      {isVisible('ctr') && <Td align="right">{pageSummary.impressions ? fmtPct(pageSummary.ctr) : '—'}</Td>}
                      {isVisible('clicks') && <Td align="right">{fmtNum(pageSummary.clicks)}</Td>}
                      {isVisible('unique_link_clicks') && <Td align="right">{pageSummary.unique_link_clicks != null ? fmtNum(pageSummary.unique_link_clicks) : '—'}</Td>}
                      {isVisible('unique_link_click_cpc') && <Td align="right">{pageSummary.unique_link_click_cpc != null ? fmtMoney(pageSummary.unique_link_click_cpc) : '—'}</Td>}
                      {isVisible('lp_clicks') && <Td align="right">{fmtNum(pageSummary.lp_clicks)}</Td>}
                      {isVisible('lp_views') && <Td align="right">{fmtNum(pageSummary.lp_views)}</Td>}
                      {isVisible('lp_ctr') && <Td align="right">{pageSummary.clicks ? fmtPct(pageSummary.lp_ctr) : '—'}</Td>}
                      {isVisible('lp_view_rate') && <Td align="right">{fmtPct(pageSummary.lp_view_rate)}</Td>}
                      {isVisible('cpm') && <Td align="right">{pageSummary.impressions ? fmtMoney(pageSummary.cpm) : '—'}</Td>}
                      {isVisible('offer_cpc') && <Td align="right">—</Td>}
                      {isVisible('offer_cr') && <Td align="right">—</Td>}
                      {isVisible('verdict') && <Td>—</Td>}
                      {isVisible('suggestion') && <Td>—</Td>}
                      {isVisible('decision') && <Td>—</Td>}
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>

            {/* Pagination controls */}
            <div className="flex flex-wrap items-center justify-between gap-3 mt-3 text-xs">
              <div className="text-muted-foreground">
                Showing <strong>{pageStart + 1}–{pageEnd}</strong> of <strong>{filtered.length}</strong>
              </div>
              <div className="flex items-center gap-2">
                <label>
                  Per page:
                  <select
                    value={pageSize}
                    onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                    className="ml-1 px-2 py-1 border border-border rounded bg-background"
                  >
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={200}>200</option>
                  </select>
                </label>
                <button
                  onClick={() => setPage(1)}
                  disabled={safePage === 1}
                  className="px-2 py-1 border border-border rounded bg-background hover:bg-muted disabled:opacity-40"
                >« First</button>
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={safePage === 1}
                  className="px-2 py-1 border border-border rounded bg-background hover:bg-muted disabled:opacity-40"
                >‹ Prev</button>
                <span className="px-2">
                  Page
                  <input
                    type="number"
                    min={1}
                    max={totalPages}
                    value={safePage}
                    onChange={e => {
                      const n = parseInt(e.target.value);
                      if (!isNaN(n)) setPage(Math.min(Math.max(1, n), totalPages));
                    }}
                    className="mx-1 w-12 px-1 py-0.5 border border-border rounded bg-background text-center"
                  />
                  of {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={safePage >= totalPages}
                  className="px-2 py-1 border border-border rounded bg-background hover:bg-muted disabled:opacity-40"
                >Next ›</button>
                <button
                  onClick={() => setPage(totalPages)}
                  disabled={safePage >= totalPages}
                  className="px-2 py-1 border border-border rounded bg-background hover:bg-muted disabled:opacity-40"
                >Last »</button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function truncate(s: string, n: number) {
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
}

function FilterSelect({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: string[] }) {
  return (
    <label className="inline-flex items-center gap-1 text-xs">
      <span className="text-muted-foreground">{label}:</span>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="px-2 py-1 border border-border rounded bg-background"
      >
        <option value="all">All</option>
        {options.map(o => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    </label>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="px-3 py-2 text-left font-medium text-xs text-muted-foreground whitespace-nowrap">{children}</th>;
}

function ThSort({
  label, k, sortKey, sortDir, onSort, align,
}: {
  label: string; k: keyof PerformanceRow; sortKey: keyof PerformanceRow; sortDir: 'asc' | 'desc'; onSort: (k: keyof PerformanceRow) => void; align?: 'right';
}) {
  const isActive = sortKey === k;
  return (
    <th className={`px-3 py-2 font-medium text-xs text-muted-foreground whitespace-nowrap ${align === 'right' ? 'text-right' : 'text-left'}`}>
      <button onClick={() => onSort(k)} className={`inline-flex items-center gap-1 hover:text-foreground ${isActive ? 'text-foreground' : ''}`}>
        {label}{isActive && (sortDir === 'asc' ? ' ↑' : ' ↓')}
      </button>
    </th>
  );
}

function Td({ children, align, className, title }: { children: React.ReactNode; align?: 'right'; className?: string; title?: string }) {
  return <td className={`px-3 py-2 whitespace-nowrap ${align === 'right' ? 'text-right' : ''} ${className || ''}`} title={title}>{children}</td>;
}

function BucketChip({
  icon, label, count, active, activeClass, idleClass, onClick,
}: { icon: string; label: string; count: number; active: boolean; activeClass: string; idleClass: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-full border transition-colors ${active ? activeClass : idleClass} ${count === 0 ? 'opacity-60' : ''}`}
      title={count === 0 ? 'No creatives currently in this bucket' : `${count} creatives in this bucket — click to filter`}
    >
      <span>{icon}</span>
      <span>{label}</span>
      <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${active ? 'bg-white/25' : 'bg-black/10 dark:bg-white/15'}`}>{count}</span>
    </button>
  );
}

function HighlightCol({
  title, accent, rows, format,
}: { title: string; accent: 'green' | 'red' | 'blue' | 'amber'; rows: HighlightRow[]; format: (v: number) => string }) {
  const accentColor = {
    green: 'text-green-700 dark:text-green-400',
    red:   'text-red-600 dark:text-red-400',
    blue:  'text-sky-700 dark:text-sky-400',
    amber: 'text-amber-700 dark:text-amber-400',
  }[accent];
  return (
    <div className="bg-card border border-border rounded p-2">
      <div className={`text-[10px] uppercase tracking-widest font-semibold ${accentColor} mb-1.5`}>{title}</div>
      {rows.length === 0 ? (
        <div className="text-[11px] text-muted-foreground italic">No data</div>
      ) : (
        <ol className="space-y-1">
          {rows.map((r, i) => (
            <li key={r.id + i} className="flex items-center justify-between gap-2 text-[11px]">
              <span className="truncate max-w-[10rem]" title={`${r.name}${r.convention ? '  ·  ' + r.convention : ''}`}>
                <span className={`font-mono ${accentColor}`}>{i + 1}.</span>{' '}
                <span className="font-mono">{r.convention || r.name || '—'}</span>
              </span>
              <span className={`font-semibold ${accentColor} flex-shrink-0`}>{format(r.value)}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Pivot tree — collapsible hierarchy. Levels (top → bottom):
//   1. Creative type (BROLL / UGC / MAP / …)
//   2. Script (S1, S2, S3 …)
//   3. State (TX, CA, GA …)
//   4. Account (friendly account name from ad_accounts)
//   5. Campaign (leaf — full metrics row)
// Each non-leaf row shows rolled-up Spend / Revenue / Profit / ROI / Conv +
// a count of campaigns underneath it. Click to expand → renders the next
// level inline indented. Variations (S3-V1, V2, …) just appear as separate
// script rows because the convention parser stores them in the editor_code
// position (S3 root + Vx not represented as variation today). We can add a
// sub-grouping later if buyers ever use a -V<n>- segment consistently.
// ────────────────────────────────────────────────────────────────────────────

interface PivotAgg {
  spend: number;
  rt_cost: number;
  revenue: number;
  profit: number;
  roi: number;
  roas: number;
  cpa: number;
  rpl: number | null;
  conversions: number;
  rt_ok: number;
  rt_pending: number;
  rt_declined: number;
  approval_rate: number | null;
  impressions: number;
  clicks: number;
  link_clicks: number;
  unique_link_clicks: number | null;
  rt_clicks: number;
  unique_clicks: number;
  lp_clicks: number;
  lp_views: number;
  ctr: number;
  unique_ctr: number;
  unique_cpc: number;
  unique_link_click_cpc: number | null;
  cpm: number;
  lp_ctr: number;
  lp_view_rate: number | null;
  rt_cpc: number | null;
  // Impression-weighted averages
  hook_rate: number | null;
  hold_rate: number | null;
  avg_play_time: number | null;
  offer_cpc: number | null;
  offer_cr: number | null;
  // Video retention counters + derived rates
  video_plays: number;
  video_p25: number;
  video_p50: number;
  video_p75: number;
  video_p100: number;
  video_p25_rate: number | null;
  video_p50_rate: number | null;
  video_p75_rate: number | null;
  video_p100_rate: number | null;
  rowCount: number;
}

interface PivotNode {
  level: 1 | 2 | 3 | 4 | 5;
  key: string;
  label: string;
  agg: PivotAgg;
  children?: PivotNode[];
  row?: PerformanceRow; // only set when level === 5 (leaf)
}

// Reorderable pivot dimensions. Leaf level (campaign rows) is always last and
// not part of this list. 'page' = FB page identifier from the convention (the
// 4th segment, e.g. P81 / PML78 / editor code).
type PivotDim = 'creative' | 'script' | 'state' | 'account' | 'page' | 'media_buyer';
const PIVOT_DIM_LABEL: Record<PivotDim, string> = {
  creative:    'Creative',
  script:      'Script',
  state:       'State',
  account:     'Account',
  page:        'Page',
  media_buyer: 'Media Buyer',
};
const PIVOT_DIM_ALL: PivotDim[] = ['creative', 'script', 'state', 'account', 'page', 'media_buyer'];
const DEFAULT_PIVOT_ORDER: PivotDim[] = ['creative', 'script', 'state', 'account'];
const pivotDimValue = (r: PerformanceRow, dim: PivotDim): string => {
  switch (dim) {
    case 'creative':    return r.creative_type || 'Unknown';
    case 'script':      return r.script_number != null ? `S${r.script_number}` : 'Unknown';
    case 'state':       return r.state || 'Unknown';
    case 'account':     return r.account_name || r.account_id || 'No account';
    case 'page':        return r.page_code || 'No page';
    case 'media_buyer': return r.media_buyer_name
                          ? `${r.media_buyer_name} (${r.media_buyer || '?'})`
                          : (r.media_buyer || 'No buyer');
  }
};

// Sortable aggregate fields for the pivot tree headers. Anything not in this
// set falls back to "spend desc" — the historical default.
type PivotSortKey =
  | 'spend' | 'revenue' | 'profit' | 'roi' | 'roas' | 'cpa' | 'rpl'
  | 'conversions' | 'rt_ok' | 'rt_pending' | 'rt_declined' | 'approval_rate'
  | 'impressions' | 'clicks' | 'unique_link_clicks' | 'unique_link_click_cpc'
  | 'lp_clicks' | 'lp_views' | 'lp_ctr' | 'lp_view_rate' | 'ctr' | 'cpm'
  | 'hook_rate' | 'hold_rate' | 'avg_play_time'
  | 'video_plays' | 'video_p25_rate' | 'video_p50_rate' | 'video_p75_rate' | 'video_p100_rate'
  | 'offer_cpc' | 'offer_cr' | 'rowCount';
type PivotSortDir = 'asc' | 'desc';

function getPivotValue(agg: PivotAgg, k: PivotSortKey): number {
  const v = (agg as any)[k];
  return typeof v === 'number' && !isNaN(v) ? v : -Infinity;
}

function buildPivotTree(
  rows: PerformanceRow[],
  order: PivotDim[] = DEFAULT_PIVOT_ORDER,
  sortKey: PivotSortKey = 'spend',
  sortDir: PivotSortDir = 'desc',
): PivotNode[] {
  // Group helper
  const groupBy = <T, K extends string>(arr: T[], key: (t: T) => K): Map<K, T[]> => {
    const m = new Map<K, T[]>();
    for (const item of arr) {
      const k = key(item);
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(item);
    }
    return m;
  };
  // Aggregator: sums every additive metric, re-derives every ratio from the
  // sums (averaging child ratios is wrong because each child has a different
  // weight). Hook/Hold/AvgPlayTime/OfferCPC/OfferCR are impression-weighted
  // (or video-plays-weighted for play time) — same logic as the SQL.
  const sumAgg = (rs: PerformanceRow[]): PivotAgg => {
    const acc: any = {
      spend: 0, rt_cost: 0, revenue: 0, conversions: 0,
      impressions: 0, clicks: 0, link_clicks: 0, unique_link_clicks: 0, unique_link_clicks_has: false,
      rt_clicks: 0, unique_clicks: 0, lp_clicks: 0, unique_lp_clicks: 0, lp_views: 0,
      rt_ok: 0, rt_pending: 0, rt_declined: 0,
      video_plays: 0, video_p25: 0, video_p50: 0, video_p75: 0, video_p100: 0,
      hook_w_sum: 0, hook_w: 0,
      hold_w_sum: 0, hold_w: 0,
      apt_w_sum: 0,  apt_w: 0,
      ocpc_w_sum: 0, ocpc_w: 0,
      ocr_w_sum: 0,  ocr_w: 0,
      rowCount: rs.length,
    };
    for (const r of rs) {
      acc.spend += r.spend;
      acc.rt_cost += r.rt_cost;
      acc.revenue += r.revenue;
      acc.conversions += r.conversions;
      acc.impressions += r.impressions;
      acc.clicks += r.clicks;
      acc.link_clicks += r.link_clicks;
      if (r.unique_link_clicks != null) { acc.unique_link_clicks += r.unique_link_clicks; acc.unique_link_clicks_has = true; }
      acc.rt_clicks += r.rt_clicks;
      acc.unique_clicks += r.unique_clicks;
      acc.lp_clicks += r.lp_clicks;
      acc.unique_lp_clicks += r.unique_lp_clicks;
      acc.lp_views += r.lp_views;
      acc.rt_ok += r.rt_ok;
      acc.rt_pending += r.rt_pending;
      acc.rt_declined += r.rt_declined;
      acc.video_plays += r.video_plays;
      acc.video_p25 += r.video_p25;
      acc.video_p50 += r.video_p50;
      acc.video_p75 += r.video_p75;
      acc.video_p100 += r.video_p100;
      // weighted averages
      if (r.hook_rate != null) { acc.hook_w_sum += r.hook_rate * r.impressions; acc.hook_w += r.impressions; }
      if (r.hold_rate != null) { acc.hold_w_sum += r.hold_rate * r.impressions; acc.hold_w += r.impressions; }
      if (r.avg_play_time != null && r.video_plays > 0) { acc.apt_w_sum += r.avg_play_time * r.video_plays; acc.apt_w += r.video_plays; }
      if (r.offer_cpc != null && r.conversions > 0)  { acc.ocpc_w_sum += r.offer_cpc * r.conversions; acc.ocpc_w += r.conversions; }
      if (r.offer_cr  != null && r.clicks > 0)       { acc.ocr_w_sum  += r.offer_cr  * r.clicks;      acc.ocr_w  += r.clicks; }
    }
    const out: PivotAgg = {
      spend: acc.spend,
      rt_cost: acc.rt_cost,
      revenue: acc.revenue,
      profit: acc.revenue - acc.spend,
      roi: acc.spend > 0 ? ((acc.revenue - acc.spend) / acc.spend) * 100 : 0,
      roas: acc.spend > 0 ? acc.revenue / acc.spend : 0,
      cpa: acc.conversions > 0 ? acc.spend / acc.conversions : 0,
      rpl: acc.conversions > 0 ? acc.revenue / acc.conversions : null,
      conversions: acc.conversions,
      rt_ok: acc.rt_ok, rt_pending: acc.rt_pending, rt_declined: acc.rt_declined,
      approval_rate: (acc.rt_ok + acc.rt_declined) > 0 ? acc.rt_ok / (acc.rt_ok + acc.rt_declined) : null,
      impressions: acc.impressions,
      clicks: acc.clicks,
      link_clicks: acc.link_clicks,
      unique_link_clicks: acc.unique_link_clicks_has ? acc.unique_link_clicks : null,
      unique_link_click_cpc: acc.unique_link_clicks_has && acc.unique_link_clicks > 0 ? acc.spend / acc.unique_link_clicks : null,
      rt_clicks: acc.rt_clicks,
      unique_clicks: acc.unique_clicks,
      lp_clicks: acc.lp_clicks,
      lp_views: acc.lp_views,
      ctr: acc.impressions > 0 ? acc.clicks / acc.impressions : 0,
      unique_ctr: acc.impressions > 0 ? acc.unique_clicks / acc.impressions : 0,
      unique_cpc: acc.unique_clicks > 0 ? acc.spend / acc.unique_clicks : 0,
      cpm: acc.impressions > 0 ? (acc.spend / acc.impressions) * 1000 : 0,
      lp_ctr: acc.clicks > 0 ? acc.lp_clicks / acc.clicks : 0,
      lp_view_rate: acc.lp_clicks > 0 ? acc.lp_views / acc.lp_clicks : null,
      rt_cpc: acc.rt_clicks > 0 ? acc.spend / acc.rt_clicks : null,
      hook_rate: acc.hook_w > 0 ? acc.hook_w_sum / acc.hook_w : null,
      hold_rate: acc.hold_w > 0 ? acc.hold_w_sum / acc.hold_w : null,
      avg_play_time: acc.apt_w > 0 ? acc.apt_w_sum / acc.apt_w : null,
      offer_cpc: acc.ocpc_w > 0 ? acc.ocpc_w_sum / acc.ocpc_w : null,
      offer_cr:  acc.ocr_w  > 0 ? acc.ocr_w_sum  / acc.ocr_w  : null,
      video_plays: acc.video_plays,
      video_p25: acc.video_p25, video_p50: acc.video_p50,
      video_p75: acc.video_p75, video_p100: acc.video_p100,
      video_p25_rate:  acc.video_plays > 0 ? acc.video_p25  / acc.video_plays : null,
      video_p50_rate:  acc.video_plays > 0 ? acc.video_p50  / acc.video_plays : null,
      video_p75_rate:  acc.video_plays > 0 ? acc.video_p75  / acc.video_plays : null,
      video_p100_rate: acc.video_plays > 0 ? acc.video_p100 / acc.video_plays : null,
      rowCount: rs.length,
    };
    return out;
  };

  // Sort comparator: respects the user-selected sort column + direction. Sorts
  // siblings within each tree level (so the user can see e.g. "highest ROI
  // creative type" at L1, then "highest ROI script" within that creative, etc.)
  // and leaf campaigns at the deepest level.
  const dirMul = sortDir === 'asc' ? 1 : -1;
  const cmp = (a: PivotAgg, b: PivotAgg) => (getPivotValue(a, sortKey) - getPivotValue(b, sortKey)) * dirMul;

  // Build the tree recursively in the order the caller asked for.
  // depth = 0 means we're producing level-1 nodes; depth = order.length means
  // we're producing leaves (one per campaign row).
  const build = (
    subset: PerformanceRow[],
    depth: number,
    parentKey: string,
  ): PivotNode[] => {
    if (depth === order.length) {
      // Leaves: one node per campaign row.
      const leaves = subset.map(r => ({
        level: (order.length + 1) as PivotNode['level'],
        key: `${parentKey}|c=${r.id}`,
        label: r.name,
        agg: sumAgg([r]),
        row: r,
      }));
      leaves.sort((a, b) => cmp(a.agg, b.agg));
      return leaves;
    }
    const dim = order[depth];
    const grouped = groupBy(subset, r => pivotDimValue(r, dim));
    const nodes = Array.from(grouped.entries()).map(([groupKey, groupRows]) => {
      const key = parentKey ? `${parentKey}|${dim}=${groupKey}` : `${dim}=${groupKey}`;
      return {
        level: (depth + 1) as PivotNode['level'],
        key,
        label: groupKey,
        agg: sumAgg(groupRows),
        children: build(groupRows, depth + 1, key),
      };
    });
    nodes.sort((a, b) => cmp(a.agg, b.agg));
    return nodes;
  };

  return build(rows, 0, '');
}

// Walk the tree and collect every key (used by "expand all" button).
function collectAllPivotKeys(rows: PerformanceRow[], order: PivotDim[] = DEFAULT_PIVOT_ORDER): Set<string> {
  const tree = buildPivotTree(rows, order);
  const out = new Set<string>();
  const walk = (n: PivotNode) => {
    out.add(n.key);
    if (n.children) n.children.forEach(walk);
  };
  tree.forEach(walk);
  return out;
}

function PivotTable({
  rows, expanded, onToggle, onExpandAll, onCollapseAll, verdictFor, order, onOrderChange,
}: {
  rows: PerformanceRow[];
  expanded: Set<string>;
  onToggle: (k: string) => void;
  onExpandAll: () => void;
  onCollapseAll: () => void;
  verdictFor: (roi: number) => { label: string; bg: string };
  order: PivotDim[];
  onOrderChange: (next: PivotDim[]) => void;
}) {
  // Click-to-sort state — applies to every level of the tree. Default = spend desc.
  const [sortKey, setSortKey] = React.useState<PivotSortKey>('spend');
  const [sortDir, setSortDir] = React.useState<PivotSortDir>('desc');
  const onSort = (k: PivotSortKey) => {
    if (sortKey === k) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(k); setSortDir('desc'); }
  };
  const arrow = (k: PivotSortKey) => sortKey === k ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';
  const sortableThClass = 'px-3 py-2 text-right font-medium text-xs text-muted-foreground whitespace-nowrap cursor-pointer hover:text-foreground select-none';

  const tree = React.useMemo(() => buildPivotTree(rows, order, sortKey, sortDir), [rows, order, sortKey, sortDir]);

  // Per-slot dropdown change: picking a dim already present in another slot
  // swaps the two so the order stays a permutation of all 4 dims.
  const onSlotChange = (slotIdx: number, next: PivotDim) => {
    const current = order[slotIdx];
    if (current === next) return;
    const conflictIdx = order.indexOf(next);
    const newOrder = [...order];
    newOrder[slotIdx] = next;
    if (conflictIdx >= 0) newOrder[conflictIdx] = current;
    onOrderChange(newOrder);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2 text-xs text-muted-foreground">
        <div className="flex flex-wrap items-center gap-1">
          <span className="mr-1">Hierarchy:</span>
          {order.map((dim, i) => (
            <React.Fragment key={i}>
              <select
                value={dim}
                onChange={(e) => onSlotChange(i, e.target.value as PivotDim)}
                className="px-2 py-0.5 text-xs border border-border rounded bg-background hover:bg-muted"
                title={`Level ${i + 1}`}
              >
                {PIVOT_DIM_ALL.map(d => (
                  <option key={d} value={d}>{PIVOT_DIM_LABEL[d]}</option>
                ))}
              </select>
              {i < order.length - 1 && <span>→</span>}
            </React.Fragment>
          ))}
          <span>→ Campaign</span>
        </div>
        <div className="flex gap-1">
          <button onClick={onExpandAll}   className="px-2 py-0.5 rounded border border-border hover:bg-muted">Expand all</button>
          <button onClick={onCollapseAll} className="px-2 py-0.5 rounded border border-border hover:bg-muted">Collapse all</button>
        </div>
      </div>
      <div className="overflow-x-auto border border-border rounded">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-xs text-muted-foreground sticky left-0 bg-muted/95 z-10" style={{ minWidth: '24rem' }}>Group / Campaign</th>
              <th className={sortableThClass} onClick={() => onSort('rowCount')} title="Click to sort by # of rows">Rows{arrow('rowCount')}</th>
              <th className={sortableThClass} onClick={() => onSort('spend')}    title="Click to sort">Spend{arrow('spend')}</th>
              <th className={sortableThClass} onClick={() => onSort('revenue')}  title="Click to sort">Revenue{arrow('revenue')}</th>
              <th className={sortableThClass} onClick={() => onSort('profit')}   title="Click to sort">Profit{arrow('profit')}</th>
              <th className={sortableThClass} onClick={() => onSort('roi')}      title="Click to sort">ROI{arrow('roi')}</th>
              <th className={sortableThClass} onClick={() => onSort('roas')}     title="Click to sort">ROAS{arrow('roas')}</th>
              <th className={sortableThClass} onClick={() => onSort('rpl')}      title="Revenue Per Lead (revenue ÷ conversions). Click to sort.">RPL{arrow('rpl')}</th>
              <th className={sortableThClass} onClick={() => onSort('conversions')} title="Click to sort">Conv{arrow('conversions')}</th>
              <th className={sortableThClass} onClick={() => onSort('rt_ok')}       title="Approved conversions (rt_ok — leads that passed RedTrack approval). Click to sort.">Approved{arrow('rt_ok')}</th>
              <th className={sortableThClass} onClick={() => onSort('rt_pending')}  title="Click to sort">Pend{arrow('rt_pending')}</th>
              <th className={sortableThClass} onClick={() => onSort('rt_declined')} title="Click to sort">Decl{arrow('rt_declined')}</th>
              <th className={sortableThClass} onClick={() => onSort('approval_rate')} title="Click to sort">Appr%{arrow('approval_rate')}</th>
              <th className={sortableThClass} onClick={() => onSort('cpa')}         title="Click to sort">CPA{arrow('cpa')}</th>
              <th className={sortableThClass} onClick={() => onSort('hook_rate')}   title="Click to sort">Hook{arrow('hook_rate')}</th>
              <th className={sortableThClass} onClick={() => onSort('hold_rate')}   title="Click to sort">Hold{arrow('hold_rate')}</th>
              <th className={sortableThClass} onClick={() => onSort('avg_play_time')} title="Click to sort">Avg Play{arrow('avg_play_time')}</th>
              <th className={sortableThClass} onClick={() => onSort('video_plays')} title="Click to sort">Plays{arrow('video_plays')}</th>
              <th className={sortableThClass} onClick={() => onSort('video_p25_rate')}  title="Click to sort">25%{arrow('video_p25_rate')}</th>
              <th className={sortableThClass} onClick={() => onSort('video_p50_rate')}  title="Click to sort">50%{arrow('video_p50_rate')}</th>
              <th className={sortableThClass} onClick={() => onSort('video_p75_rate')}  title="Click to sort">75%{arrow('video_p75_rate')}</th>
              <th className={sortableThClass} onClick={() => onSort('video_p100_rate')} title="Click to sort">100%{arrow('video_p100_rate')}</th>
              <th className={sortableThClass} onClick={() => onSort('impressions')} title="Click to sort">Impr{arrow('impressions')}</th>
              <th className={sortableThClass} onClick={() => onSort('ctr')}         title="Click to sort">CTR{arrow('ctr')}</th>
              <th className={sortableThClass} onClick={() => onSort('clicks')}      title="Click to sort">Clicks{arrow('clicks')}</th>
              <th className={sortableThClass} onClick={() => onSort('unique_link_clicks')}    title="Click to sort">U-Link Clicks{arrow('unique_link_clicks')}</th>
              <th className={sortableThClass} onClick={() => onSort('unique_link_click_cpc')} title="Click to sort">CPC (U-Link){arrow('unique_link_click_cpc')}</th>
              <th className={sortableThClass} onClick={() => onSort('lp_clicks')}   title="Click to sort">LP Clicks{arrow('lp_clicks')}</th>
              <th className={sortableThClass} onClick={() => onSort('lp_views')}    title="Click to sort">LP Views{arrow('lp_views')}</th>
              <th className={sortableThClass} onClick={() => onSort('lp_ctr')}      title="Click to sort">LP CTR{arrow('lp_ctr')}</th>
              <th className={sortableThClass} onClick={() => onSort('lp_view_rate')} title="Click to sort">LP View %{arrow('lp_view_rate')}</th>
              <th className={sortableThClass} onClick={() => onSort('cpm')}         title="Click to sort">CPM{arrow('cpm')}</th>
              <th className={sortableThClass} onClick={() => onSort('offer_cpc')}   title="Click to sort">Offer CPC{arrow('offer_cpc')}</th>
              <th className={sortableThClass} onClick={() => onSort('offer_cr')}    title="Click to sort">Offer CR{arrow('offer_cr')}</th>
              <th className="px-3 py-2 text-left font-medium text-xs text-muted-foreground">Verdict</th>
            </tr>
          </thead>
          <tbody>
            {tree.map(node => (
              <PivotRowGroup key={node.key} node={node} expanded={expanded} onToggle={onToggle} verdictFor={verdictFor} />
            ))}
          </tbody>
          {/* Pivot totals — sum of every level-1 aggregate. Ratios re-derived
              from the sums (averaging child ratios would weight every group
              equally). Sticky so it stays visible while scrolling. */}
          {tree.length > 0 && (() => {
            const t = tree.reduce((acc, n) => {
              acc.spend += n.agg.spend;
              acc.revenue += n.agg.revenue;
              acc.profit += n.agg.profit;
              acc.conversions += n.agg.conversions;
              acc.rt_ok += n.agg.rt_ok;
              acc.rt_pending += n.agg.rt_pending;
              acc.rt_declined += n.agg.rt_declined;
              acc.impressions += n.agg.impressions;
              acc.clicks += n.agg.clicks;
              acc.lp_clicks += n.agg.lp_clicks;
              acc.lp_views += n.agg.lp_views;
              acc.video_plays += n.agg.video_plays;
              acc.video_p25 += n.agg.video_p25;
              acc.video_p50 += n.agg.video_p50;
              acc.video_p75 += n.agg.video_p75;
              acc.video_p100 += n.agg.video_p100;
              acc.rowCount += n.agg.rowCount;
              if (n.agg.unique_link_clicks != null) {
                acc.unique_link_clicks = (acc.unique_link_clicks || 0) + n.agg.unique_link_clicks;
              }
              return acc;
            }, {
              spend: 0, revenue: 0, profit: 0, conversions: 0,
              rt_ok: 0, rt_pending: 0, rt_declined: 0,
              impressions: 0, clicks: 0, lp_clicks: 0, lp_views: 0,
              video_plays: 0, video_p25: 0, video_p50: 0, video_p75: 0, video_p100: 0,
              unique_link_clicks: null as number | null, rowCount: 0,
            });
            const roi  = t.spend > 0 ? ((t.revenue - t.spend) / t.spend) * 100 : 0;
            const roas = t.spend > 0 ? t.revenue / t.spend : 0;
            const cpa  = t.conversions > 0 ? t.spend / t.conversions : 0;
            const rpl  = t.conversions > 0 ? t.revenue / t.conversions : null;
            const apr  = (t.rt_ok + t.rt_declined) > 0 ? t.rt_ok / (t.rt_ok + t.rt_declined) : null;
            const ctr  = t.impressions > 0 ? t.clicks / t.impressions : 0;
            const cpm  = t.impressions > 0 ? (t.spend / t.impressions) * 1000 : 0;
            const lpCtr = t.clicks > 0 ? t.lp_clicks / t.clicks : 0;
            const lpView = t.lp_clicks > 0 ? t.lp_views / t.lp_clicks : null;
            const ulcCpc = t.unique_link_clicks && t.unique_link_clicks > 0 ? t.spend / t.unique_link_clicks : null;
            const v25 = t.video_plays > 0 ? t.video_p25 / t.video_plays : null;
            const v50 = t.video_plays > 0 ? t.video_p50 / t.video_plays : null;
            const v75 = t.video_plays > 0 ? t.video_p75 / t.video_plays : null;
            const v100 = t.video_plays > 0 ? t.video_p100 / t.video_plays : null;
            const money = (n: number) => `$${Math.round(n).toLocaleString()}`;
            const num   = (n: number) => Math.round(n).toLocaleString();
            const pct   = (n: number | null) => n == null ? '—' : `${(n * 100).toFixed(1)}%`;
            return (
              <tfoot className="sticky bottom-0 bg-card border-t-2 border-border z-10 shadow-[0_-2px_4px_rgba(0,0,0,0.06)]">
                <tr className="font-semibold text-xs">
                  <td className="px-3 py-2 whitespace-nowrap sticky left-0 bg-card z-10">
                    <span className="uppercase tracking-wider text-muted-foreground">Pivot totals</span>
                    <span className="ml-2 text-muted-foreground">{tree.length} groups · {t.rowCount} rows</span>
                  </td>
                  <td className="px-3 py-2 text-right">{t.rowCount.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right text-red-700 dark:text-red-400">{money(t.spend)}</td>
                  <td className="px-3 py-2 text-right text-green-700 dark:text-green-400">{money(t.revenue)}</td>
                  <td className={`px-3 py-2 text-right ${t.profit < 0 ? 'text-red-600 dark:text-red-400' : 'text-sky-700 dark:text-sky-400'}`}>{money(t.profit)}</td>
                  <td className={`px-3 py-2 text-right ${roi < 0 ? 'text-red-600 dark:text-red-400' : roi > 40 ? 'text-green-700 dark:text-green-400' : 'text-amber-700 dark:text-amber-400'}`}>{`${roi.toFixed(1)}%`}</td>
                  <td className="px-3 py-2 text-right">{`${roas.toFixed(2)}x`}</td>
                  <td className="px-3 py-2 text-right">{rpl == null ? '—' : money(rpl)}</td>
                  <td className="px-3 py-2 text-right">{num(t.conversions)}</td>
                  <td className="px-3 py-2 text-right text-green-700 dark:text-green-400">{num(t.rt_ok)}</td>
                  <td className="px-3 py-2 text-right text-amber-700 dark:text-amber-400">{num(t.rt_pending)}</td>
                  <td className="px-3 py-2 text-right text-red-700 dark:text-red-400">{num(t.rt_declined)}</td>
                  <td className="px-3 py-2 text-right">{pct(apr)}</td>
                  <td className="px-3 py-2 text-right">{t.conversions ? money(cpa) : '—'}</td>
                  <td className="px-3 py-2 text-right">—</td>
                  <td className="px-3 py-2 text-right">—</td>
                  <td className="px-3 py-2 text-right">—</td>
                  <td className="px-3 py-2 text-right">{num(t.video_plays)}</td>
                  <td className="px-3 py-2 text-right">{pct(v25)}</td>
                  <td className="px-3 py-2 text-right">{pct(v50)}</td>
                  <td className="px-3 py-2 text-right">{pct(v75)}</td>
                  <td className="px-3 py-2 text-right">{pct(v100)}</td>
                  <td className="px-3 py-2 text-right">{num(t.impressions)}</td>
                  <td className="px-3 py-2 text-right">{t.impressions ? pct(ctr) : '—'}</td>
                  <td className="px-3 py-2 text-right">{num(t.clicks)}</td>
                  <td className="px-3 py-2 text-right">{t.unique_link_clicks != null ? num(t.unique_link_clicks) : '—'}</td>
                  <td className="px-3 py-2 text-right">{ulcCpc != null ? money(ulcCpc) : '—'}</td>
                  <td className="px-3 py-2 text-right">{num(t.lp_clicks)}</td>
                  <td className="px-3 py-2 text-right">{num(t.lp_views)}</td>
                  <td className="px-3 py-2 text-right">{t.clicks ? pct(lpCtr) : '—'}</td>
                  <td className="px-3 py-2 text-right">{pct(lpView)}</td>
                  <td className="px-3 py-2 text-right">{t.impressions ? money(cpm) : '—'}</td>
                  <td className="px-3 py-2 text-right">—</td>
                  <td className="px-3 py-2 text-right">—</td>
                  <td className="px-3 py-2 text-left">—</td>
                </tr>
              </tfoot>
            );
          })()}
        </table>
      </div>
    </div>
  );
}

function PivotRowGroup({
  node, expanded, onToggle, verdictFor,
}: {
  node: PivotNode;
  expanded: Set<string>;
  onToggle: (k: string) => void;
  verdictFor: (roi: number) => { label: string; bg: string };
}) {
  const isOpen = expanded.has(node.key);
  const isLeaf = node.level === 5;
  const indent = (node.level - 1) * 16; // 16px per level
  const levelBg = [
    'bg-indigo-50/50 dark:bg-indigo-900/20',     // L1
    'bg-blue-50/40 dark:bg-blue-900/15',         // L2
    'bg-teal-50/40 dark:bg-teal-900/15',         // L3
    'bg-amber-50/30 dark:bg-amber-900/15',       // L4
    '',                                          // L5 leaf
  ][node.level - 1] || '';
  const v = verdictFor(node.agg.roi);
  return (
    <>
      <tr
        className={`border-t border-border ${isLeaf ? 'hover:bg-muted/30' : `${levelBg} hover:opacity-95 cursor-pointer`}`}
        onClick={() => !isLeaf && onToggle(node.key)}
      >
        <td className={`px-3 py-2 whitespace-nowrap sticky left-0 z-10 ${isLeaf ? 'bg-card' : levelBg}`}>
          <div className="flex items-center gap-1" style={{ paddingLeft: indent }}>
            {!isLeaf ? (
              <span className="inline-block w-4 text-xs">{isOpen ? '▼' : '▶'}</span>
            ) : (
              <span className="inline-block w-4" />
            )}
            <span className={isLeaf ? 'font-mono text-xs' : 'font-semibold text-xs'} title={node.label}>
              {isLeaf
                ? <span className="inline-block max-w-[20rem] truncate align-middle">{node.label}</span>
                : node.label}
            </span>
            {!isLeaf && (
              <span className="text-[10px] text-muted-foreground ml-2">({node.agg.rowCount} rows)</span>
            )}
          </div>
        </td>
        <td className="px-3 py-2 text-right text-muted-foreground whitespace-nowrap">{isLeaf ? '' : node.agg.rowCount}</td>
        <td className="px-3 py-2 text-right text-red-700 dark:text-red-400 whitespace-nowrap">{node.agg.spend ? `$${formatNumber0(node.agg.spend)}` : '—'}</td>
        <td className="px-3 py-2 text-right text-green-700 dark:text-green-400 whitespace-nowrap">{node.agg.revenue ? `$${formatNumber0(node.agg.revenue)}` : '—'}</td>
        <td className={`px-3 py-2 text-right font-medium whitespace-nowrap ${node.agg.profit < 0 ? 'text-red-600 dark:text-red-400' : node.agg.profit > 0 ? 'text-sky-700 dark:text-sky-400' : ''}`}>
          {node.agg.spend || node.agg.revenue ? `$${formatNumber0(node.agg.profit)}` : '—'}
        </td>
        <td className={`px-3 py-2 text-right font-medium whitespace-nowrap ${node.agg.roi < 0 ? 'text-red-600 dark:text-red-400' : node.agg.roi > 40 ? 'text-green-700 dark:text-green-400' : node.agg.roi > 20 ? 'text-sky-700 dark:text-sky-400' : 'text-amber-700 dark:text-amber-400'}`}>
          {node.agg.spend ? `${node.agg.roi.toFixed(1)}%` : '—'}
        </td>
        <td className="px-3 py-2 text-right whitespace-nowrap">{node.agg.spend ? `${node.agg.roas.toFixed(2)}x` : '—'}</td>
        <td className="px-3 py-2 text-right whitespace-nowrap">{node.agg.conversions && node.agg.rpl != null ? `$${node.agg.rpl.toFixed(2)}` : '—'}</td>
        <td className="px-3 py-2 text-right whitespace-nowrap">{node.agg.conversions ? node.agg.conversions.toLocaleString() : '—'}</td>
        <td className="px-3 py-2 text-right text-green-700 dark:text-green-400 whitespace-nowrap">{node.agg.rt_ok || '—'}</td>
        <td className="px-3 py-2 text-right text-amber-700 dark:text-amber-400 whitespace-nowrap">{node.agg.rt_pending || '—'}</td>
        <td className="px-3 py-2 text-right text-red-700 dark:text-red-400 whitespace-nowrap">{node.agg.rt_declined || '—'}</td>
        <td className="px-3 py-2 text-right whitespace-nowrap">{node.agg.approval_rate != null ? `${(node.agg.approval_rate * 100).toFixed(1)}%` : '—'}</td>
        <td className="px-3 py-2 text-right whitespace-nowrap">{node.agg.conversions ? `$${node.agg.cpa.toFixed(2)}` : '—'}</td>
        <td className="px-3 py-2 text-right whitespace-nowrap">{node.agg.hook_rate != null ? `${(node.agg.hook_rate * 100).toFixed(1)}%` : '—'}</td>
        <td className="px-3 py-2 text-right whitespace-nowrap">{node.agg.hold_rate != null ? `${(node.agg.hold_rate * 100).toFixed(1)}%` : '—'}</td>
        <td className="px-3 py-2 text-right whitespace-nowrap">{node.agg.avg_play_time != null ? `${node.agg.avg_play_time.toFixed(1)}s` : '—'}</td>
        <td className="px-3 py-2 text-right whitespace-nowrap">{node.agg.video_plays ? formatNumber0(node.agg.video_plays) : '—'}</td>
        <td className="px-3 py-2 text-right whitespace-nowrap">{node.agg.video_p25_rate  != null ? `${(node.agg.video_p25_rate  * 100).toFixed(1)}%` : '—'}</td>
        <td className="px-3 py-2 text-right whitespace-nowrap">{node.agg.video_p50_rate  != null ? `${(node.agg.video_p50_rate  * 100).toFixed(1)}%` : '—'}</td>
        <td className="px-3 py-2 text-right whitespace-nowrap">{node.agg.video_p75_rate  != null ? `${(node.agg.video_p75_rate  * 100).toFixed(1)}%` : '—'}</td>
        <td className="px-3 py-2 text-right whitespace-nowrap">{node.agg.video_p100_rate != null ? `${(node.agg.video_p100_rate * 100).toFixed(1)}%` : '—'}</td>
        <td className="px-3 py-2 text-right whitespace-nowrap">{node.agg.impressions ? formatNumber0(node.agg.impressions) : '—'}</td>
        <td className="px-3 py-2 text-right whitespace-nowrap">{node.agg.impressions ? `${(node.agg.ctr * 100).toFixed(2)}%` : '—'}</td>
        <td className="px-3 py-2 text-right whitespace-nowrap">{node.agg.clicks ? formatNumber0(node.agg.clicks) : '—'}</td>
        <td className="px-3 py-2 text-right whitespace-nowrap">{node.agg.unique_link_clicks != null ? formatNumber0(node.agg.unique_link_clicks) : '—'}</td>
        <td className="px-3 py-2 text-right whitespace-nowrap">{node.agg.unique_link_click_cpc != null ? `$${node.agg.unique_link_click_cpc.toFixed(2)}` : '—'}</td>
        <td className="px-3 py-2 text-right whitespace-nowrap">{node.agg.lp_clicks ? formatNumber0(node.agg.lp_clicks) : '—'}</td>
        <td className="px-3 py-2 text-right whitespace-nowrap">{node.agg.lp_views ? formatNumber0(node.agg.lp_views) : '—'}</td>
        <td className="px-3 py-2 text-right whitespace-nowrap">{node.agg.clicks ? `${(node.agg.lp_ctr * 100).toFixed(2)}%` : '—'}</td>
        <td className="px-3 py-2 text-right whitespace-nowrap">{node.agg.lp_view_rate != null ? `${(node.agg.lp_view_rate * 100).toFixed(1)}%` : '—'}</td>
        <td className="px-3 py-2 text-right whitespace-nowrap">{node.agg.impressions ? `$${node.agg.cpm.toFixed(2)}` : '—'}</td>
        <td className="px-3 py-2 text-right whitespace-nowrap">{node.agg.offer_cpc != null ? `$${node.agg.offer_cpc.toFixed(2)}` : '—'}</td>
        <td className="px-3 py-2 text-right whitespace-nowrap">{node.agg.offer_cr  != null ? `${(node.agg.offer_cr * 100).toFixed(1)}%` : '—'}</td>
        <td className="px-3 py-2 whitespace-nowrap">
          <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${v.bg}`}>{v.label}</span>
        </td>
      </tr>
      {isOpen && node.children && node.children.map(c => (
        <PivotRowGroup key={c.key} node={c} expanded={expanded} onToggle={onToggle} verdictFor={verdictFor} />
      ))}
    </>
  );
}
